// @ts-nocheck
// TODO: Add proper null checks for balance and userAccountData variables
/**
 * Unified Tydro Trading Dashboard
 * Matches ProfileWallet design - one seamless container with expandable sections
 */

import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { useAccount, useBalance, useWalletClient, usePublicClient } from 'wagmi';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { formatUnits, parseUnits } from 'viem';
import { useTydro } from '../../hooks/useTydro';
import { useSupabaseTokens } from '../../hooks/useSupabaseTokens';
import { useAllTokens } from '../../hooks/useAllTokens';
import { useRelayPrice } from '../../hooks/useRelayPrice';
import { getUserHoldings, addUserHolding, removeUserHolding } from '../../utils/supabase';
import { SwapWidget } from '@relayprotocol/relay-kit-ui';
import { adaptViemWallet } from '@relayprotocol/relay-sdk';
import { ethers } from 'ethers6';
import AssetCard from './AssetCard';

// Asset Cards Grid Container
const AssetCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  width: 100%;
  max-width: 100%;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

// Matching ProfileWallet Container
const Container = styled.div`
  width: 100%;
  margin-top: 24px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 12px;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 16px 8px;
    border-radius: 0;
    margin-top: 0;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;

const Title = styled.h3`
  margin: 0;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 700;
`;

const RefreshButton = styled.button`
  background: rgba(88, 28, 135, 0.8);
  border: none;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(88, 28, 135, 1);
    transform: rotate(90deg);
  }
  
  &:active {
    transform: rotate(180deg);
  }
`;

const ActionBarWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
`;

const ActionBar = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
  
  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const ActionButton = styled.button`
  padding: 8px 12px;
  background: rgba(88, 28, 135, 0.8);
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  
  &:hover { background: rgba(88, 28, 135, 1); }
  &:disabled { background: rgba(80,80,90,0.6); cursor: not-allowed; }
  
  @media (max-width: 768px) {
    padding: 10px 14px;
    font-size: 13px;
    min-width: 60px;
  }
`;

const Table = styled.div`
  margin-top: 16px;
  overflow-x: auto;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 2fr 120px 100px 1fr;
  gap: 16px;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #333333;
  max-width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: 1.2fr 70px 60px 90px;
    gap: 6px;
    padding: 10px 0;
    font-size: 11px;
  }
`;

const Cell = styled.div`
  color: #e5e7eb;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 768px) {
    font-size: 12px;
    gap: 6px;
  }
`;

const TokenIconCell = styled(Cell)`
  gap: 10px;
`;

const TokenIcon = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #2a2a2a;
`;

const TokenIconPlaceholder = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #a855f7, #7c3aed);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
`;

const TokenTextContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const TokenName = styled.span`
  font-weight: 600;
  color: #fff;
`;

const TokenSymbol = styled.span`
  font-size: 11px;
  color: #9ca3af;
`;

const AmountCell = styled(Cell)`
  font-family: monospace;
  font-weight: 600;
  justify-content: flex-end;
  text-align: right;
`;

const USDCell = styled(Cell)`
  font-family: monospace;
  color: #10b981;
  font-weight: 600;
  justify-content: flex-end;
  text-align: right;
`;

const ActionCell = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 4px;
    flex-direction: column;
    align-items: stretch;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: #000000;
  margin: 24px 0;
`;

const ExpandableSection = styled.div<{ show: boolean }>`
  max-height: ${props => props.show ? '5000px' : '0'};
  overflow: hidden;
  transition: max-height 0.4s ease;
`;

// Asset Toggle (ETH vs WETH)
const AssetToggle = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const AssetButton = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 8px 16px;
  background: ${props => props.$active ? '#333333' : 'rgba(0, 0, 0, 0.3)'};
  border: 1px solid ${props => props.$active ? '#a855f7' : '#333333'};
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #444444;
  }
`;

// Token Search Components
const TokenSearchInput = styled.input`
  width: 100%;
  padding: 12px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 10px;
  color: #fff;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  margin-bottom: 12px;
  
  &::placeholder {
    color: #6b7280;
  }
  
  &:focus {
    outline: none;
    border-color: #a855f7;
  }
`;

const TokenList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 10px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
  }
  
  &::-webkit-scrollbar-thumb {
    background: #444444;
    border-radius: 3px;
  }
`;

const TokenListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s;
  border-bottom: 1px solid #333333;
  
  &:hover {
    background: #000000;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const SelectedTokenCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 12px;
  margin-bottom: 16px;
`;

const SelectedTokenLogo = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #2a2a2a;
`;

const SelectedTokenInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SelectedTokenName = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #fff;
`;

const SelectedTokenSymbol = styled.div`
  font-size: 13px;
  color: #9ca3af;
  font-family: monospace;
`;

const SelectedTokenAddress = styled.div`
  font-size: 11px;
  color: #6b7280;
  font-family: monospace;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CopyButton = styled.button`
  background: rgba(88, 28, 135, 0.6);
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  color: #fff;
  font-size: 10px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(88, 28, 135, 0.8);
  }
  
  i {
    margin-right: 4px;
  }
`;

// Debt Coverage Indicator
const DebtCoverageCard = styled.div<{ $status: 'safe' | 'warning' | 'danger' }>`
  background: ${props => 
    props.$status === 'safe' ? 'rgba(16, 185, 129, 0.1)' :
    props.$status === 'warning' ? 'rgba(245, 158, 11, 0.1)' :
    'rgba(239, 68, 68, 0.1)'
  };
  border: 1px solid ${props =>
    props.$status === 'safe' ? 'rgba(16, 185, 129, 0.3)' :
    props.$status === 'warning' ? 'rgba(245, 158, 11, 0.3)' :
    'rgba(239, 68, 68, 0.3)'
  };
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DebtCoverageLabel = styled.div`
  font-size: 13px;
  color: #9ca3af;
  font-weight: 500;
`;

const DebtCoverageValue = styled.div<{ $status: 'safe' | 'warning' | 'danger' }>`
  font-size: 16px;
  font-weight: 700;
  color: ${props =>
    props.$status === 'safe' ? '#10b981' :
    props.$status === 'warning' ? '#f59e0b' :
    '#ef4444'
  };
`;

// Add Token Row
const AddTokenRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  padding: 12px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  border: 1px dashed #333333;
`;

const AddTokenInput = styled.input`
  flex: 1;
  padding: 10px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 10px;
  color: #fff;
  font-size: 13px;
  font-family: monospace;
  
  &::placeholder {
    color: #6b7280;
  }
  
  &:focus {
    outline: none;
    border-color: #a855f7;
  }
`;

// Center swap widget and selected token when visible
const CenteredSwapWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  
  /* Center the SelectedTokenCard too */
  ${SelectedTokenCard} {
    width: 100%;
  }
`;

// Format token amount with subscript for very small amounts
const formatTokenAmount = (value: number): React.ReactNode => {
  if (value === 0) return '0.0000';
  if (value < 0.0001) {
    // For very small values, use subscript notation
    const valueStr = value.toFixed(18); // Get enough decimal places
    const match = valueStr.match(/^0\.0*([1-9])/);
    if (match) {
      const zeros = valueStr.indexOf(match[1]) - 2; // Number of zeros after decimal
      const significantDigits = valueStr.slice(valueStr.indexOf(match[1]), valueStr.indexOf(match[1]) + 2);
      return (
        <>
          0.0<sub>{zeros}</sub>{significantDigits}
        </>
      );
    }
  }
  return value.toFixed(4);
};

const TydroDashboard: React.FC = () => {
  const { address, isConnected } = useAccount();
  const { openConnectModal } = useConnectModal();
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();

  const {
    userAccountData,
    supplyETHCollateral,
    supplyWETHCollateral,
    supplyUSDT0Collateral,
    supplyKBTCCollateral,
    borrowUSDT,
    borrowAsset,
    repayUSDT,
    withdrawETH,
    withdrawAsset,
    getATokenBalance,
    getUSDT0DebtBalance,
    calculateSafeBorrowAmount,
    calculateHealthFactor,
    fetchUserAccountData,
    protocolStats,
    WETH_ADDRESS,
    USDT0_ADDRESS,
    KBTC_ADDRESS,
    TYDRO_POOL_ADDRESS
  } = useTydro();

  // Kaijin Ika address
  const IKA_ADDRESS = '0x9fa192F485c2edbFF420e068854b4d476d454FC7';

  // Refresh key to force balance refetch
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Fetch balances (will refetch when refreshKey changes)
  const { data: ethBalance, refetch: refetchEth } = useBalance({ address, query: { refetchInterval: false } });
  const { data: wethBalance, refetch: refetchWeth } = useBalance({ address, token: WETH_ADDRESS as `0x${string}`, query: { refetchInterval: false } });
  const { data: usdtBalance, refetch: refetchUsdt } = useBalance({ address, token: USDT0_ADDRESS as `0x${string}`, query: { refetchInterval: false } });
  const { data: kbtcBalance, refetch: refetchKbtc } = useBalance({ address, token: KBTC_ADDRESS as `0x${string}`, query: { refetchInterval: false } });
  const { data: ikaBalance, refetch: refetchIka } = useBalance({ address, token: IKA_ADDRESS as `0x${string}`, query: { refetchInterval: false } });
  
  // Fetch Mavrk tokens for search
  const { tokens } = useSupabaseTokens();
  const { allTokens } = useAllTokens();
  
  // User's custom token holdings
  const [holdingTokens, setHoldingTokens] = useState<Array<{address: string; symbol: string; name: string; logo?: string}>>([]);
  const [holdingBalances, setHoldingBalances] = useState<Record<string, string>>({});
  const [addTokenAddress, setAddTokenAddress] = useState('');

  // Section visibility
  const [showBuy, setShowBuy] = useState(false);
  const [showSell, setShowSell] = useState(false);
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [tradeMode, setTradeMode] = useState<'buy' | 'sell'>('buy');
  const [showSupplyBorrow] = useState(false); // Kept for ExpandableSection (not used in UI)
  const [showRepay] = useState(false); // Kept for ExpandableSection (not used in UI)
  
  // Asset section visibility
  const [showAvailableToSupply, setShowAvailableToSupply] = useState(false);
  const [showCollateralHoldings, setShowCollateralHoldings] = useState(false);
  const [showTokenPositions, setShowTokenPositions] = useState(false);
  
  // Supply modal state
  const [showSupplyModal, setShowSupplyModal] = useState(false);
  const [supplyModalAsset, setSupplyModalAsset] = useState<'ETH' | 'WETH' | 'USDT0' | 'kBTC' | null>(null);
  const [modalSupplyAmount, setModalSupplyAmount] = useState('');
  
  // Borrow modal state
  const [showBorrowModal, setShowBorrowModal] = useState(false);
  const [borrowModalAsset, setBorrowModalAsset] = useState<'USDT0' | 'kBTC' | null>(null);
  const [modalBorrowAmount, setModalBorrowAmount] = useState('');
  
  // Unified Manage modal state (Repay & Withdraw combined)
  const [showManageModal, setShowManageModal] = useState(false);
  const [manageModalAsset, setManageModalAsset] = useState<'WETH' | 'USDT0' | 'kBTC' | null>(null);
  const [manageModalTab, setManageModalTab] = useState<'repay' | 'withdraw'>('repay');
  const [modalRepayAmount, setModalRepayAmount] = useState('');
  const [modalWithdrawAmount, setModalWithdrawAmount] = useState('');
  
  // Supply cap data
  const [supplyCapData, setSupplyCapData] = useState<{
    [key: string]: {
      supplyCap: bigint;
      totalSupplied: bigint;
      availableToSupply: bigint;
      capReached: boolean;
    };
  }>({});

  // Supply/Borrow/Repay/Withdraw state
  const [supplyAsset, setSupplyAsset] = useState<'ETH' | 'WETH' | 'USDT0' | 'kBTC'>('ETH'); // Toggle between all collateral options
  const [supplyAmount, setSupplyAmount] = useState('');
  const [borrowAmount, setBorrowAmount] = useState('');
  const [repayAmount, setRepayAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [executing, setExecuting] = useState(false);
  
  // Token search state
  const [buyTokenSearch, setBuyTokenSearch] = useState('');
  const [sellTokenSearch, setSellTokenSearch] = useState('');
  const [selectedBuyToken, setSelectedBuyToken] = useState<string | null>(null);
  const [selectedSellToken, setSelectedSellToken] = useState<string | null>(null);
  
  // Asset selection for Buy/Sell/Borrow/Withdraw (USDT0 or kBTC)
  const [selectedBuyAsset, setSelectedBuyAsset] = useState<'USDT0' | 'kBTC'>('USDT0');
  const [selectedSellAsset, setSelectedSellAsset] = useState<'USDT0' | 'kBTC'>('USDT0');
  const [selectedBorrowAsset, setSelectedBorrowAsset] = useState<'USDT0' | 'kBTC'>('USDT0');
  const [selectedWithdrawAsset, setSelectedWithdrawAsset] = useState<'WETH' | 'USDT0' | 'kBTC'>('WETH');

  const [ethPrice] = useState(3300);
  const [btcPrice] = useState(95000); // BTC price for kBTC (same as Bitcoin)

  const hasPosition = userAccountData && (userAccountData.totalCollateralUSD > 0 || userAccountData.totalDebtUSD > 0);

  // State for real aToken balances and debt balance
  const [aTokenBalance, setATokenBalance] = useState<bigint>(BigInt(0)); // aWETH
  const [aUSDT0Balance, setAUSDT0Balance] = useState<bigint>(BigInt(0)); // aUSDT0
  const [aKBTCBalance, setAKBTCBalance] = useState<bigint>(BigInt(0)); // akBTC
  const [debtBalance, setDebtBalance] = useState<bigint>(BigInt(0));

  // Fetch all aToken balances using TydroProtocolDataProvider
  const fetchAllATokenBalances = async () => {
    if (!publicClient || !address) return;
    
    try {
      console.log('💰 [Tydro] Fetching aToken addresses from TydroProtocolDataProvider...');

      // TydroProtocolDataProvider contract address
      const TYDRO_DATA_PROVIDER = '0x96086C25d13943C80Ff9a19791a40Df6aFC08328' as `0x${string}`;

      // Fetch the reserve token data which includes aToken addresses
      const [wethReserveTokens, usdt0ReserveTokens, kbtcReserveTokens] = await Promise.all([
        publicClient.readContract({
          address: TYDRO_DATA_PROVIDER,
          abi: [
            {
              name: 'getReserveTokensAddresses',
              type: 'function',
              stateMutability: 'view',
              inputs: [{ name: 'asset', type: 'address' }],
              outputs: [
                { name: 'aTokenAddress', type: 'address' },
                { name: 'stableDebtTokenAddress', type: 'address' },
                { name: 'variableDebtTokenAddress', type: 'address' }
              ]
            }
          ],
          functionName: 'getReserveTokensAddresses',
          args: [WETH_ADDRESS]
        }),
        publicClient.readContract({
          address: TYDRO_DATA_PROVIDER,
          abi: [
            {
              name: 'getReserveTokensAddresses',
              type: 'function',
              stateMutability: 'view',
              inputs: [{ name: 'asset', type: 'address' }],
              outputs: [
                { name: 'aTokenAddress', type: 'address' },
                { name: 'stableDebtTokenAddress', type: 'address' },
                { name: 'variableDebtTokenAddress', type: 'address' }
              ]
            }
          ],
          functionName: 'getReserveTokensAddresses',
          args: [USDT0_ADDRESS]
        }),
        publicClient.readContract({
          address: TYDRO_DATA_PROVIDER,
          abi: [
            {
              name: 'getReserveTokensAddresses',
              type: 'function',
              stateMutability: 'view',
              inputs: [{ name: 'asset', type: 'address' }],
              outputs: [
                { name: 'aTokenAddress', type: 'address' },
                { name: 'stableDebtTokenAddress', type: 'address' },
                { name: 'variableDebtTokenAddress', type: 'address' }
              ]
            }
          ],
          functionName: 'getReserveTokensAddresses',
          args: [KBTC_ADDRESS]
        })
      ]);

      // Extract aToken addresses (index 0 in the returned tuple)
      const aWETH = (wethReserveTokens as unknown as any[])[0] as `0x${string}`;
      const aUSDT0 = (usdt0ReserveTokens as unknown as any[])[0] as `0x${string}`;
      const aKBTC = (kbtcReserveTokens as unknown as any[])[0] as `0x${string}`;

      console.log('📍 [Tydro] aToken addresses from TydroProtocolDataProvider:');
      console.log('  aWETH:', aWETH);
      console.log('  aUSDT0:', aUSDT0);
      console.log('  akBTC:', aKBTC);

      // Now fetch balances from the correct aToken contracts
      const [wethBalance, usdt0Balance, kbtcBalance] = await Promise.all([
        publicClient.readContract({
          address: aWETH,
          abi: [
            {
              inputs: [{ name: 'account', type: 'address' }],
              name: 'balanceOf',
              outputs: [{ name: '', type: 'uint256' }],
              stateMutability: 'view',
              type: 'function'
            }
          ],
          functionName: 'balanceOf',
          args: [address]
        }),
        publicClient.readContract({
          address: aUSDT0,
          abi: [
            {
              inputs: [{ name: 'account', type: 'address' }],
              name: 'balanceOf',
              outputs: [{ name: '', type: 'uint256' }],
              stateMutability: 'view',
              type: 'function'
            }
          ],
          functionName: 'balanceOf',
          args: [address]
        }),
        publicClient.readContract({
          address: aKBTC,
          abi: [
            {
              inputs: [{ name: 'account', type: 'address' }],
              name: 'balanceOf',
              outputs: [{ name: '', type: 'uint256' }],
              stateMutability: 'view',
              type: 'function'
            }
          ],
          functionName: 'balanceOf',
          args: [address]
        })
      ]);

      setATokenBalance(wethBalance as bigint);
      setAUSDT0Balance(usdt0Balance as bigint);
      setAKBTCBalance(kbtcBalance as bigint);

      const wethBalanceNum = parseFloat(formatUnits(wethBalance as bigint, 18));
      const usdt0BalanceNum = parseFloat(formatUnits(usdt0Balance as bigint, 6));
      const kbtcBalanceNum = parseFloat(formatUnits(kbtcBalance as bigint, 8));
      
      const wethUSD = wethBalanceNum * ethPrice;
      const usdt0USD = usdt0BalanceNum; // USDT is 1:1 with USD
      const kbtcUSD = kbtcBalanceNum * btcPrice;

      console.log('💰 [Tydro] aWETH balance:', wethBalanceNum, `($${wethUSD.toFixed(2)})`);
      console.log('💚 [Tydro] aUSDT0 balance:', usdt0BalanceNum, `($${usdt0USD.toFixed(2)})`);
      console.log('🟡 [Tydro] akBTC balance:', kbtcBalanceNum, 'akBTC', `($${kbtcUSD.toFixed(2)})`);
      console.log('📊 [Tydro] Total aToken value: $' + (wethUSD + usdt0USD + kbtcUSD).toFixed(2));
    } catch (err) {
      console.error('❌ Error fetching aToken balances:', err);
    }
  };

  // Backwards compatible wrapper
  const fetchATokenBalance = async () => {
    await fetchAllATokenBalances();
  };

  // Fetch supply cap data for all assets
  const fetchSupplyCapData = async () => {
    if (!publicClient) return;
    
    try {
      const TYDRO_DATA_PROVIDER = '0x96086C25d13943C80Ff9a19791a40Df6aFC08328' as `0x${string}`;
      
      console.log('📊 [Tydro] Fetching supply cap data...');

      // Fetch reserve configuration for each asset
      const assets = [
        { address: WETH_ADDRESS, symbol: 'WETH', decimals: 18 },
        { address: USDT0_ADDRESS, symbol: 'USDT0', decimals: 6 },
        { address: KBTC_ADDRESS, symbol: 'kBTC', decimals: 8 }
      ];

      const capData: any = {};

      for (const asset of assets) {
        try {
          // Get reserve caps using the simple getReserveCaps function
          const reserveCaps = await publicClient.readContract({
            address: TYDRO_DATA_PROVIDER,
            abi: [
              {
                name: 'getReserveCaps',
                type: 'function',
                stateMutability: 'view',
                inputs: [{ name: 'asset', type: 'address' }],
                outputs: [
                  { name: 'borrowCap', type: 'uint256' },
                  { name: 'supplyCap', type: 'uint256' }
                ]
              }
            ],
            functionName: 'getReserveCaps',
            args: [asset.address]
          });

          // Get reserve data (includes total supplied)
          const reserveData = await publicClient.readContract({
            address: TYDRO_DATA_PROVIDER,
            abi: [
              {
                name: 'getReserveData',
                type: 'function',
                stateMutability: 'view',
                inputs: [{ name: 'asset', type: 'address' }],
                outputs: [
                  { name: 'unbacked', type: 'uint256' },
                  { name: 'accruedToTreasuryScaled', type: 'uint256' },
                  { name: 'totalAToken', type: 'uint256' },
                  { name: 'totalStableDebt', type: 'uint256' },
                  { name: 'totalVariableDebt', type: 'uint256' },
                  { name: 'liquidityRate', type: 'uint256' },
                  { name: 'variableBorrowRate', type: 'uint256' },
                  { name: 'stableBorrowRate', type: 'uint256' },
                  { name: 'averageStableBorrowRate', type: 'uint256' },
                  { name: 'liquidityIndex', type: 'uint256' },
                  { name: 'variableBorrowIndex', type: 'uint256' },
                  { name: 'lastUpdateTimestamp', type: 'uint40' }
                ]
              }
            ],
            functionName: 'getReserveData',
            args: [asset.address]
          });

          const supplyCap = (reserveCaps as unknown as any[])[1] as bigint; // supplyCap from getReserveCaps
          const totalSupplied = (reserveData as unknown as any[])[2] as bigint; // totalAToken
          
          // Convert supply cap to proper units (it's returned in whole units, not wei)
          const supplyCapInUnits = supplyCap * BigInt(10 ** asset.decimals);

          const availableToSupply = supplyCapInUnits > totalSupplied 
            ? supplyCapInUnits - totalSupplied 
            : BigInt(0);

          const capReached = totalSupplied >= supplyCapInUnits || supplyCap === BigInt(0);

          capData[asset.symbol] = {
            supplyCap: supplyCapInUnits,
            totalSupplied,
            availableToSupply,
            capReached
          };

          console.log(`📊 [${asset.symbol}] Cap: ${formatUnits(supplyCapInUnits, asset.decimals)}, Supplied: ${formatUnits(totalSupplied, asset.decimals)}, Available: ${formatUnits(availableToSupply, asset.decimals)}, Utilization: ${supplyCapInUnits > BigInt(0) ? ((Number(totalSupplied) / Number(supplyCapInUnits)) * 100).toFixed(2) : 0}%`);
        } catch (err) {
          console.error(`❌ Error fetching cap data for ${asset.symbol}:`, err);
        }
      }

      setSupplyCapData(capData);
    } catch (err) {
      console.error('❌ Error fetching supply cap data:', err);
    }
  };

  // Fetch real debt balance
  const fetchDebtBalance = async () => {
    if (!getUSDT0DebtBalance) return;
    const balance = await getUSDT0DebtBalance();
    setDebtBalance(balance);
  };

  // Fetch balances on mount and after actions
  React.useEffect(() => {
    fetchATokenBalance();
    fetchDebtBalance();
    fetchSupplyCapData();
  }, [userAccountData]);

  // Load user's custom token holdings
  React.useEffect(() => {
    (async () => {
      if (!address) {
        setHoldingTokens([]);
        return;
      }
      const holdings = await getUserHoldings(address);
      const rows: Array<{address: string; symbol: string; name: string; logo?: string}> = [];
      for (const addr of holdings) {
        const match = allTokens.find(t => t.contract_address?.toLowerCase() === addr.toLowerCase());
        if (match) {
          rows.push({
            address: match.contract_address!,
            symbol: (match.symbol || 'TOKEN').toUpperCase(),
            name: match.name || 'Token',
            logo: match.logo_url || undefined
          });
        } else {
          rows.push({
            address: addr,
            symbol: 'TOKEN',
            name: addr.slice(0,6)+'...'+addr.slice(-4)
          });
        }
      }
      setHoldingTokens(rows);
    })();
  }, [address, allTokens, refreshKey]);

  // Build pricing input for Relay API
  const pricingTokens = useMemo(() => {
    // Include IKA as a default token for pricing
    const ikaToken = {
      id: IKA_ADDRESS,
      name: 'Kaijin Ika',
      symbol: 'IKA',
      contract_address: IKA_ADDRESS,
      network: 'ink' as const,
    };
    
    const customTokens = holdingTokens
      .filter(t => !!t.address)
      .map(t => ({
        id: t.address,
        name: t.name,
        symbol: t.symbol,
        contract_address: t.address,
        network: 'ink' as const,
      }));
    
    return [ikaToken, ...customTokens];
  }, [holdingTokens, IKA_ADDRESS]);

  const { getPriceForToken } = useRelayPrice(pricingTokens as any);

  // Fetch balances for custom holdings
  React.useEffect(() => {
    (async () => {
      if (!address || holdingTokens.length === 0) return;

      const balances: Record<string, string> = {};
      
      // Simple balance fetching using fetch to RPC
      for (const token of holdingTokens) {
        try {
          const response = await fetch('https://rpc-gel.inkonchain.com/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              jsonrpc: '2.0',
              id: 1,
              method: 'eth_call',
              params: [{
                to: token.address,
                data: `0x70a08231000000000000000000000000${address.slice(2)}` // balanceOf(address)
              }, 'latest']
            })
          });
          
          const data = await response.json();
          if (data.result) {
            const balanceBigInt = BigInt(data.result);
            balances[token.address] = formatUnits(balanceBigInt, 18);
          }
        } catch (err) {
          console.error(`Failed to fetch balance for ${token.address}:`, err);
          balances[token.address] = '0';
        }
      }
      
      setHoldingBalances(balances);
    })();
  }, [address, holdingTokens, refreshKey]);

  // Calculate max withdrawable ETH using REAL aToken balance
  const maxWithdrawableETH = useMemo(() => {
    if (!userAccountData || aTokenBalance === BigInt(0)) return 0;
    
    const debtUSD = userAccountData.totalDebtUSD;
    const aTokenBalanceETH = parseFloat(formatUnits(aTokenBalance, 18));
    
    if (debtUSD === 0) {
      // No debt - can withdraw all aTokens
      return aTokenBalanceETH;
    }
    
    // With debt - need to maintain HF > 2 for safety
    // Max withdraw = (Collateral - (Debt / 0.85 * 2)) / ethPrice
    const minCollateralNeeded = (debtUSD / 0.85) * 2;
    const maxWithdrawUSD = Math.max(0, userAccountData.totalCollateralUSD - minCollateralNeeded);
    const maxWithdrawETH = maxWithdrawUSD / ethPrice;
    
    // Return the MINIMUM of calculated safe amount and actual aToken balance
    return Math.min(maxWithdrawETH, aTokenBalanceETH);
  }, [userAccountData, aTokenBalance, ethPrice]);

  // Filter tokens for Buy search (use allTokens for complete catalog)
  const filteredBuyTokens = useMemo(() => {
    if (!buyTokenSearch) return [];
    const search = buyTokenSearch.toLowerCase();
    return allTokens.filter(token => 
      token.name.toLowerCase().includes(search) ||
      token.symbol.toLowerCase().includes(search) ||
      token.contract_address?.toLowerCase().includes(search)
    ).slice(0, 10); // Limit to 10 results for performance
  }, [allTokens, buyTokenSearch]);

  // Filter tokens for Sell search (use allTokens for complete catalog)
  const filteredSellTokens = useMemo(() => {
    if (!sellTokenSearch) return [];
    const search = sellTokenSearch.toLowerCase();
    return allTokens.filter(token => 
      token.name.toLowerCase().includes(search) ||
      token.symbol.toLowerCase().includes(search) ||
      token.contract_address?.toLowerCase().includes(search)
    ).slice(0, 10); // Limit to 10 results for performance
  }, [allTokens, sellTokenSearch]);

  // Get selected token data (use allTokens for lookup)
  const selectedBuyTokenData = useMemo(() => 
    allTokens.find(t => t.contract_address === selectedBuyToken),
    [allTokens, selectedBuyToken]
  );

  const selectedSellTokenData = useMemo(() => 
    allTokens.find(t => t.contract_address === selectedSellToken),
    [allTokens, selectedSellToken]
  );

  // Calculate total asset value (USDT0 + IKA + all custom tokens) vs debt
  const debtCoverage = useMemo(() => {
    if (!userAccountData || userAccountData.totalDebtUSD === 0) {
      return { status: 'safe' as const, coverage: 0, totalAssets: 0 };
    }

    // Start with USDT0 balance
    let totalAssetValue = usdtBalance ? parseFloat(formatUnits(usdtBalance.value, 6)) : 0;

    // Add Kaijin Ika (IKA) balance
    if (ikaBalance) {
      const ikaBalanceNum = parseFloat(formatUnits(ikaBalance.value, 18));
      const ikaPrice = getPriceForToken?.(IKA_ADDRESS) || 0;
      totalAssetValue += (ikaBalanceNum * ikaPrice);
    }

    // Add value of all custom holdings
    for (const token of holdingTokens) {
      const balance = holdingBalances[token.address] || '0';
      const balanceNum = parseFloat(balance);
      if (balanceNum > 0) {
        const priceUSD = getPriceForToken?.(token.address) || 0;
        totalAssetValue += (balanceNum * priceUSD);
      }
    }

    const debt = userAccountData.totalDebtUSD;
    const coverageRatio = debt > 0 ? (totalAssetValue / debt) * 100 : 0;

    let status: 'safe' | 'warning' | 'danger';
    if (coverageRatio >= 100) {
      status = 'safe'; // Assets cover debt
    } else if (coverageRatio >= 80) {
      status = 'warning'; // Close to underwater
    } else {
      status = 'danger'; // Underwater
    }

    return { status, coverage: coverageRatio, totalAssets: totalAssetValue };
  }, [userAccountData, usdtBalance, ikaBalance, holdingTokens, holdingBalances, getPriceForToken, IKA_ADDRESS]);

  const handleRefresh = async () => {
    // Refetch all Tydro data
    await fetchUserAccountData();
    await fetchATokenBalance();
    await fetchDebtBalance();
    
    // Refetch wallet balances
    await refetchEth();
    await refetchWeth();
    await refetchUsdt();
    await refetchKbtc();
    await refetchIka();
    
    // Increment refresh key to reload holdings and prices
    setRefreshKey(prev => prev + 1);
  };

  const handleSupply = async () => {
    if (!supplyAmount || parseFloat(supplyAmount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    try {
      setExecuting(true);
      
      // Supply ETH, WETH, USDT0, or kBTC based on user selection
      if (supplyAsset === 'ETH') {
        await supplyETHCollateral(supplyAmount);
        alert('ETH Supplied!');
      } else if (supplyAsset === 'WETH') {
        await supplyWETHCollateral(supplyAmount);
        alert('WETH Supplied!');
      } else if (supplyAsset === 'USDT0') {
        await supplyUSDT0Collateral(supplyAmount);
        alert('USDT0 Supplied!');
      } else if (supplyAsset === 'kBTC') {
        await supplyKBTCCollateral(supplyAmount);
        alert('kBTC Supplied!');
      }
      
      // CRITICAL: Comprehensive state refresh after transaction
      await new Promise(resolve => setTimeout(resolve, 2000)); // Wait for blockchain to settle
      await fetchUserAccountData();
      await fetchATokenBalance();
      await fetchDebtBalance();
      await refetchEth();
      await refetchWeth();
      await refetchUsdt();
      await refetchKbtc();
      await refetchIka();
      setRefreshKey(prev => prev + 1);
      
      setSupplyAmount('');
    } catch (error: any) {
      alert('Transaction Error: Please try again. Check your wallet transaction history for more information.');
    } finally {
      setExecuting(false);
    }
  };

  const handleBorrow = async () => {
    if (!borrowAmount || parseFloat(borrowAmount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    try {
      setExecuting(true);
      
      // Borrow selected asset (USDT0 or kBTC)
      const assetAddress = selectedBorrowAsset === 'USDT0' ? USDT0_ADDRESS : KBTC_ADDRESS;
      const decimals = selectedBorrowAsset === 'USDT0' ? 6 : 8;
      
      await borrowAsset(assetAddress, borrowAmount, decimals, selectedBorrowAsset);
      alert(`${selectedBorrowAsset} Borrowed!`);
      
      // CRITICAL: Comprehensive state refresh after transaction
      await new Promise(resolve => setTimeout(resolve, 2000));
      await fetchUserAccountData();
      await fetchATokenBalance();
      await fetchDebtBalance();
      await refetchEth();
      await refetchWeth();
      await refetchUsdt();
      await refetchKbtc();
      await refetchIka();
      setRefreshKey(prev => prev + 1);
      
      setBorrowAmount('');
    } catch (error: any) {
      alert('Transaction Error: Please try again. Check your wallet transaction history for more information.');
    } finally {
      setExecuting(false);
    }
  };

  const handleRepay = async () => {
    if (!repayAmount || parseFloat(repayAmount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    try {
      setExecuting(true);
      
      // Check if user is trying to repay all debt (clicked MAX button)
      const actualDebt = parseFloat(formatUnits(debtBalance, 6));
      const repayingAmount = parseFloat(repayAmount);
      const isRepayingAll = Math.abs(repayingAmount - actualDebt) < 0.000001; // Within tolerance
      
      // If repaying all, pass true to use uint256.max (handles interest accrual)
      await repayUSDT(repayAmount, isRepayingAll);
      alert('USDT0 Repaid!');
      
      // CRITICAL: Comprehensive state refresh after transaction
      await new Promise(resolve => setTimeout(resolve, 2000));
      await fetchUserAccountData();
      await fetchATokenBalance();
      await fetchDebtBalance();
      await refetchEth();
      await refetchWeth();
      await refetchUsdt();
      await refetchKbtc();
      await refetchIka();
      setRefreshKey(prev => prev + 1);
      
      setRepayAmount('');
    } catch (error: any) {
      alert('Transaction Error: Please try again. Check your wallet transaction history for more information.');
    } finally {
      setExecuting(false);
    }
  };

  const handleWithdraw = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    try {
      setExecuting(true);
      
      // Get asset details based on selection
      const assetAddress = selectedWithdrawAsset === 'WETH' ? WETH_ADDRESS 
                          : selectedWithdrawAsset === 'USDT0' ? USDT0_ADDRESS 
                          : KBTC_ADDRESS;
      const decimals = selectedWithdrawAsset === 'WETH' ? 18 
                      : selectedWithdrawAsset === 'USDT0' ? 6 
                      : 8;
      
      // Check if user clicked MAX (withdrawing all)
      // For now, just use the withdrawAmount as-is
      // TODO: Add specific aToken balance check per asset
      const isWithdrawingAll = false; // We'll implement this properly later
      
      // Withdraw selected asset
      await withdrawAsset(assetAddress, withdrawAmount, decimals, selectedWithdrawAsset, isWithdrawingAll);
      alert(`${selectedWithdrawAsset} Withdrawn!`);
      
      // CRITICAL: Comprehensive state refresh after transaction
      await new Promise(resolve => setTimeout(resolve, 2000));
      await fetchUserAccountData();
      await fetchATokenBalance();
      await fetchDebtBalance();
      await refetchEth();
      await refetchWeth();
      await refetchUsdt();
      await refetchKbtc();
      await refetchIka();
      setRefreshKey(prev => prev + 1);
      
      setWithdrawAmount('');
    } catch (error: any) {
      alert('Transaction Error: Please try again. Check your wallet transaction history for more information.');
    } finally {
      setExecuting(false);
    }
  };

  // Calculate total portfolio value (excludes collateral assets)
  const totalPortfolioValue = useMemo(() => {
    let total = 0;
    
    // Native ETH
    if (ethBalance) total += parseFloat(formatUnits(ethBalance.value, 18)) * ethPrice;
    // WETH
    if (wethBalance) total += parseFloat(formatUnits(wethBalance.value, 18)) * ethPrice;
    // kBTC
    if (kbtcBalance) total += parseFloat(formatUnits(kbtcBalance.value, 8)) * btcPrice;
    // USDT0
    if (usdtBalance) total += parseFloat(formatUnits(usdtBalance.value, 6));
    // IKA
    if (ikaBalance) total += parseFloat(formatUnits(ikaBalance.value, 18)) * (getPriceForToken?.(IKA_ADDRESS) || 0);
    // Custom tokens
    holdingTokens.forEach(token => {
      const balance = parseFloat(holdingBalances[token.address] || '0');
      const price = getPriceForToken?.(token.address) || 0;
      total += balance * price;
    });
    
    return total;
  }, [ethBalance, wethBalance, kbtcBalance, usdtBalance, ikaBalance, holdingTokens, holdingBalances, ethPrice, btcPrice, getPriceForToken, IKA_ADDRESS]);

  if (!isConnected) {
    return (
      <Container>
        <Title>TYDRO LEVERAGED TRADING</Title>
        <p style={{ color: '#9ca3af', marginTop: '16px' }}>Connect your wallet to access leveraged trading</p>
        <ActionButton onClick={openConnectModal} style={{ marginTop: '16px' }}>
          Connect Wallet
        </ActionButton>
      </Container>
    );
  }

  return (
    <>
      {/* Asset Cards Grid - 2x2 on desktop, 1x4 on mobile */}
      <AssetCardsGrid>
        {/* kBTC Card */}
        <AssetCard
          assetSymbol="kBTC"
          assetName="Kraken Wrapped Bitcoin"
          logo="/landing-assets/kBTC_logo.png"
          balance={kbtcBalance ? parseFloat(formatUnits(kbtcBalance.value, 8)).toFixed(8) : '0.00000000'}
          aTokenBalance={parseFloat(formatUnits(aKBTCBalance, 8)).toFixed(8)}
          aTokenSymbol="akBTC"
          supplyAPY={protocolStats?.assetRates?.['kBTC']?.supplyAPY || '0.00'}
          borrowAPY={protocolStats?.assetRates?.['kBTC']?.borrowAPY || '0.00'}
          tydroInkPoints="0.066"
          symbolColor="#F7931A"
          apyColor="#F7931A"
          onSupply={() => {
            setSupplyModalAsset('kBTC');
            setModalSupplyAmount('');
            setShowSupplyModal(true);
          }}
          onBorrow={() => {
            setBorrowModalAsset('kBTC');
            setModalBorrowAmount('');
            setShowBorrowModal(true);
          }}
          onManage={() => {
            setManageModalAsset('kBTC');
            setModalRepayAmount('');
            setModalWithdrawAmount('');
            if (userAccountData && userAccountData.totalDebtUSD > 0) {
              setManageModalTab('repay');
            } else {
              setManageModalTab('withdraw');
            }
            setShowManageModal(true);
          }}
          onTrade={() => {
            setTradeMode('buy');
            setShowTradeModal(true);
          }}
          hasCollateral={!!(userAccountData && userAccountData.totalCollateralUSD > 0)}
          hasDebt={!!(userAccountData && userAccountData.totalDebtUSD > 0)}
          hasATokenBalance={aKBTCBalance > BigInt(0)}
        />
        
        {/* ETH Card */}
        <AssetCard
          assetSymbol="ETH"
          assetName="Ethereum"
          logo="/landing-assets/ETH_logo.png"
          balance={ethBalance ? parseFloat(formatUnits(ethBalance.value, 18)).toFixed(4) : '0.0000'}
          aTokenBalance={parseFloat(formatUnits(aTokenBalance, 18)).toFixed(4)}
          aTokenSymbol="aWETH"
          supplyAPY={protocolStats?.assetRates?.['WETH']?.supplyAPY || '0.00'}
          borrowAPY={protocolStats?.assetRates?.['WETH']?.borrowAPY || '0.00'}
          tydroInkPoints="0.126"
          symbolColor="#627EEA"
          apyColor="#627EEA"
          onSupply={() => {
            setSupplyModalAsset('ETH');
            setModalSupplyAmount('');
            setShowSupplyModal(true);
          }}
          onBorrow={() => {
            // Open borrow modal with USDT0 (default borrowable asset)
            setBorrowModalAsset('USDT0');
            setModalBorrowAmount('');
            setShowBorrowModal(true);
          }}
          onManage={() => {
            setManageModalAsset('WETH');
            setModalRepayAmount('');
            setModalWithdrawAmount('');
            if (userAccountData && userAccountData.totalDebtUSD > 0) {
              setManageModalTab('repay');
            } else {
              setManageModalTab('withdraw');
            }
            setShowManageModal(true);
          }}
          onTrade={() => {
            setTradeMode('buy');
            setShowTradeModal(true);
          }}
          hasCollateral={!!(userAccountData && userAccountData.totalCollateralUSD > 0)}
          hasDebt={!!(userAccountData && userAccountData.totalDebtUSD > 0)}
          hasATokenBalance={aTokenBalance > BigInt(0)}
        />
        
        {/* WETH Card */}
        <AssetCard
          assetSymbol="WETH"
          assetName="Wrapped ETH"
          logo="/landing-assets/WETH_logo.png"
          balance={wethBalance ? parseFloat(formatUnits(wethBalance.value, 18)).toFixed(4) : '0.0000'}
          aTokenBalance={parseFloat(formatUnits(aTokenBalance, 18)).toFixed(4)}
          aTokenSymbol="aWETH"
          supplyAPY={protocolStats?.assetRates?.['WETH']?.supplyAPY || '0.00'}
          borrowAPY={protocolStats?.assetRates?.['WETH']?.borrowAPY || '0.00'}
          tydroInkPoints="0.126"
          symbolColor="#627EEA"
          apyColor="#627EEA"
          onSupply={() => {
            setSupplyModalAsset('WETH');
            setModalSupplyAmount('');
            setShowSupplyModal(true);
          }}
          onBorrow={() => {
            // Open borrow modal with USDT0 (default borrowable asset)
            setBorrowModalAsset('USDT0');
            setModalBorrowAmount('');
            setShowBorrowModal(true);
          }}
          onManage={() => {
            setManageModalAsset('WETH');
            setModalRepayAmount('');
            setModalWithdrawAmount('');
            if (userAccountData && userAccountData.totalDebtUSD > 0) {
              setManageModalTab('repay');
            } else {
              setManageModalTab('withdraw');
            }
            setShowManageModal(true);
          }}
          onTrade={() => {
            setTradeMode('buy');
            setShowTradeModal(true);
          }}
          hasCollateral={!!(userAccountData && userAccountData.totalCollateralUSD > 0)}
          hasDebt={!!(userAccountData && userAccountData.totalDebtUSD > 0)}
          hasATokenBalance={aTokenBalance > BigInt(0)}
        />
        
        {/* USDT0 Card */}
        <AssetCard
          assetSymbol="USDT0"
          assetName="Bridged Tether"
          logo="/USDT0_logo.png"
          balance={usdtBalance ? parseFloat(formatUnits(usdtBalance.value, 6)).toFixed(2) : '0.00'}
          aTokenBalance={parseFloat(formatUnits(aUSDT0Balance, 6)).toFixed(2)}
          aTokenSymbol="aUSDT0"
          supplyAPY={protocolStats?.assetRates?.['USDT0']?.supplyAPY || '0.00'}
          borrowAPY={protocolStats?.assetRates?.['USDT0']?.borrowAPY || '0.00'}
          tydroInkPoints="0.192"
          symbolColor="#26A17B"
          apyColor="#26A17B"
          onSupply={() => {
            setSupplyModalAsset('USDT0');
            setModalSupplyAmount('');
            setShowSupplyModal(true);
          }}
          onBorrow={() => {
            setBorrowModalAsset('USDT0');
            setModalBorrowAmount('');
            setShowBorrowModal(true);
          }}
          onManage={() => {
            setManageModalAsset('USDT0');
            setModalRepayAmount('');
            setModalWithdrawAmount('');
            if (userAccountData && userAccountData.totalDebtUSD > 0) {
              setManageModalTab('repay');
            } else {
              setManageModalTab('withdraw');
            }
            setShowManageModal(true);
          }}
          onTrade={() => {
            setTradeMode('buy');
            setShowTradeModal(true);
          }}
          hasCollateral={!!(userAccountData && userAccountData.totalCollateralUSD > 0)}
          hasDebt={!!(userAccountData && userAccountData.totalDebtUSD > 0)}
          hasATokenBalance={aUSDT0Balance > BigInt(0)}
        />
      </AssetCardsGrid>

      {/* TYDRO LEVERAGED TRADING section removed - now showing only the 4 market cards */}
      
      {/* The following leveraged trading content is conditionally hidden */}
      {false && (
        <>
        <Table>
        {/* Holdings Section */}
        <Row 
          onClick={() => setShowTokenPositions(!showTokenPositions)}
          style={{ 
            fontWeight: 600, 
            color: '#f59e0b', 
            borderBottom: '2px solid rgba(245, 158, 11, 0.3)',
            cursor: 'pointer',
            padding: '12px 8px',
            marginTop: '8px'
          }}
        >
          <Cell style={{ display: 'flex', alignItems: 'center', gap: '8px', gridColumn: '1 / -1' }}>
            <span style={{ fontSize: '18px' }}>{showTokenPositions ? '▼' : '▶'}</span>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 700 }}>Holdings</div>
              <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 400, marginTop: '2px', lineHeight: '1.3' }}>
                View all tokens in your wallet.
              </div>
            </div>
          </Cell>
        </Row>

        {showTokenPositions && (
          <>
            {/* Column Headers for Holdings Section */}
            <Row style={{ fontWeight: 600, color: '#9ca3af', fontSize: '11px', borderBottom: '1px solid rgba(245, 158, 11, 0.2)' }}>
              <Cell>Asset</Cell>
              <Cell style={{ justifyContent: 'flex-end', textAlign: 'right' }}>Amount</Cell>
              <Cell style={{ justifyContent: 'flex-end', textAlign: 'right' }}>USD</Cell>
              <Cell></Cell>
            </Row>

            {/* ETH */}
            <Row>
              <TokenIconCell>
                <TokenIcon src="/landing-assets/ETH_logo.png" alt="ETH" />
                <TokenTextContainer>
                  <TokenName>Ethereum</TokenName>
                  <TokenSymbol>ETH</TokenSymbol>
                </TokenTextContainer>
              </TokenIconCell>
              <AmountCell>
                {ethBalance ? parseFloat(formatUnits(ethBalance.value, 18)).toFixed(4) : '0.0000'}
              </AmountCell>
              <USDCell>
                ${ethBalance ? (parseFloat(formatUnits(ethBalance.value, 18)) * ethPrice).toFixed(2) : '0.00'}
              </USDCell>
              <Cell></Cell>
            </Row>

            {/* WETH */}
            <Row>
              <TokenIconCell>
                <TokenIcon src="/landing-assets/WETH_logo.png" alt="WETH" />
                <TokenTextContainer>
                  <TokenName>Wrapped ETH</TokenName>
                  <TokenSymbol>WETH</TokenSymbol>
                </TokenTextContainer>
              </TokenIconCell>
              <AmountCell>
                {wethBalance ? parseFloat(formatUnits(wethBalance.value, 18)).toFixed(4) : '0.0000'}
              </AmountCell>
              <USDCell>
                ${wethBalance ? (parseFloat(formatUnits(wethBalance.value, 18)) * ethPrice).toFixed(2) : '0.00'}
              </USDCell>
              <Cell></Cell>
            </Row>

            {/* kBTC */}
            <Row>
              <TokenIconCell>
                <TokenIcon src="/landing-assets/kBTC_logo.png" alt="kBTC" />
                <TokenTextContainer>
                  <TokenName>Kraken Wrapped Bitcoin</TokenName>
                  <TokenSymbol>kBTC</TokenSymbol>
                </TokenTextContainer>
              </TokenIconCell>
              <AmountCell>
                {kbtcBalance ? parseFloat(formatUnits(kbtcBalance.value, 8)).toFixed(8) : '0.00000000'}
              </AmountCell>
              <USDCell>
                ${kbtcBalance ? (parseFloat(formatUnits(kbtcBalance.value, 8)) * btcPrice).toFixed(2) : '0.00'}
              </USDCell>
              <Cell></Cell>
            </Row>

            {/* USDT0 */}
            <Row>
              <TokenIconCell>
                <TokenIcon src="/USDT0_logo.png" alt="USDT0" />
                <TokenTextContainer>
                  <TokenName>Bridged Tether</TokenName>
                  <TokenSymbol>USDT0</TokenSymbol>
                </TokenTextContainer>
              </TokenIconCell>
              <AmountCell>
                {usdtBalance ? parseFloat(formatUnits(usdtBalance.value, 6)).toFixed(2) : '0.00'}
              </AmountCell>
              <USDCell>
                ${usdtBalance ? parseFloat(formatUnits(usdtBalance.value, 6)).toFixed(2) : '0.00'}
              </USDCell>
              <Cell></Cell>
            </Row>

            {/* Kaijin Ika */}
            <Row>
              <TokenIconCell>
                <TokenIcon 
                  src={allTokens.find(t => t.contract_address?.toLowerCase() === IKA_ADDRESS.toLowerCase())?.logo_url || '/landing-assets/mavrk_logo_white.png'} 
                  alt="IKA" 
                />
                <TokenTextContainer>
                  <TokenName>Kaijin Ika</TokenName>
                  <TokenSymbol>IKA</TokenSymbol>
                </TokenTextContainer>
              </TokenIconCell>
              <AmountCell>
                {ikaBalance ? formatTokenAmount(parseFloat(formatUnits(ikaBalance.value, 18))) : '0.00'}
              </AmountCell>
              <USDCell>
                ${ikaBalance ? ((parseFloat(formatUnits(ikaBalance.value, 18)) * (getPriceForToken?.(IKA_ADDRESS) || 0)).toFixed(2)) : '0.00'}
              </USDCell>
              <Cell></Cell>
            </Row>

            {/* Custom tokens from holdingTokens */}
            {holdingTokens.map((token) => {
              const balance = holdingBalances[token.address] || '0';
              const balanceNum = parseFloat(balance);
              const priceUSD = getPriceForToken?.(token.address) || 0;
              const totalUSD = balanceNum * priceUSD;

              return (
                <Row key={token.address}>
                  <TokenIconCell>
                    {token.logo ? (
                      <TokenIcon src={token.logo} alt={token.symbol} />
                    ) : (
                      <TokenIconPlaceholder>{token.symbol.charAt(0)}</TokenIconPlaceholder>
                    )}
                    <TokenTextContainer>
                      <TokenName>{token.name}</TokenName>
                      <TokenSymbol>{token.symbol}</TokenSymbol>
                    </TokenTextContainer>
                  </TokenIconCell>
                  <AmountCell>{formatTokenAmount(balanceNum)}</AmountCell>
                  <USDCell>${totalUSD.toFixed(2)}</USDCell>
                  <Cell style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      onClick={async () => {
                        if (!address) return;
                        if (!confirm(`Remove ${token.symbol} from tracking?`)) return;
                        const ok = await removeUserHolding(address, token.address);
                        if (ok) {
                          alert(`${token.symbol} removed!`);
                          setRefreshKey(prev => prev + 1); // Reload holdings
                        } else {
                          alert('Failed to remove token');
                        }
                      }}
                      style={{
                        padding: '4px 10px',
                        background: 'rgba(239, 68, 68, 0.2)',
                        border: '1px solid rgba(239, 68, 68, 0.4)',
                        borderRadius: '6px',
                        color: '#ef4444',
                        fontSize: '11px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                      }}
                    >
                      Remove
                    </button>
                  </Cell>
                </Row>
              );
            })}

            {/* Holdings Total Row */}
            <Row style={{ borderTop: '2px solid rgba(245, 158, 11, 0.3)', paddingTop: '12px', fontWeight: 700 }}>
              <Cell style={{ gridColumn: '1 / 3', color: '#f59e0b' }}>
                Total Holdings Value
              </Cell>
              <USDCell style={{ fontSize: '15px', color: '#10b981' }}>
                ${(
                  (ethBalance ? parseFloat(formatUnits(ethBalance.value, 18)) * ethPrice : 0) +
                  (wethBalance ? parseFloat(formatUnits(wethBalance.value, 18)) * ethPrice : 0) +
                  (kbtcBalance ? parseFloat(formatUnits(kbtcBalance.value, 8)) * btcPrice : 0) +
                  (usdtBalance ? parseFloat(formatUnits(usdtBalance.value, 6)) : 0) +
                  (ikaBalance ? parseFloat(formatUnits(ikaBalance.value, 18)) * (getPriceForToken?.(IKA_ADDRESS) || 0) : 0) +
                  holdingTokens.reduce((sum, token) => {
                    const balance = parseFloat(holdingBalances[token.address] || '0');
                    const price = getPriceForToken?.(token.address) || 0;
                    return sum + (balance * price);
                  }, 0)
                ).toFixed(2)}
              </USDCell>
              <Cell></Cell>
            </Row>
          </>
        )}
      </Table>

      {/* Add Token UI */}
      <AddTokenRow>
        <AddTokenInput
          type="text"
          placeholder="Enter token contract address to track..."
          value={addTokenAddress}
          onChange={(e) => setAddTokenAddress(e.target.value)}
        />
        <button
          onClick={async () => {
            if (!address) return alert('Connect wallet first');
            if (!ethers.isAddress(addTokenAddress)) return alert('Invalid address');
            const ok = await addUserHolding(address, addTokenAddress);
            if (ok) {
              alert('Token added!');
              setAddTokenAddress('');
              setRefreshKey(prev => prev + 1); // Reload holdings
            } else {
              alert('Failed to add token');
            }
          }}
          disabled={!addTokenAddress}
          style={{
            padding: '10px 16px',
            background: addTokenAddress ? 'linear-gradient(135deg, #a855f7, #7c3aed)' : 'rgba(80,80,90,0.6)',
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            fontSize: '12px',
            fontWeight: 600,
            cursor: addTokenAddress ? 'pointer' : 'not-allowed',
            whiteSpace: 'nowrap'
          }}
        >
          Add Token
        </button>
      </AddTokenRow>

      {/* Position Info Table */}
      {hasPosition && (
        <>
          <Divider />
          
          {/* Dust Position Helper */}
          {userAccountData && userAccountData.totalCollateralUSD < 0.01 && userAccountData.totalDebtUSD === 0 && (
            <div style={{
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ fontSize: '13px', color: '#f59e0b' }}>
                Dust position detected (${userAccountData.totalCollateralUSD.toFixed(8)}). Click below to clear it completely.
              </div>
              <button
                onClick={async () => {
                  if (!confirm('This will withdraw ALL remaining collateral including dust. Continue?')) return;
                  try {
                    setExecuting(true);
                    await withdrawETH('0', true); // withdrawAll = true
                    await fetchATokenBalance();
                    alert('All Collateral Withdrawn!');
                  } catch (error: any) {
                    alert('Transaction Error: Please try again. Check your wallet transaction history for more information.');
                  } finally {
                    setExecuting(false);
                  }
                }}
                disabled={executing}
                style={{
                  padding: '8px 16px',
                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  opacity: executing ? 0.5 : 1
                }}
              >
                Clear Dust
              </button>
            </div>
          )}
          
          <Row style={{ fontWeight: 600, color: '#fff', borderBottom: '2px solid #333333' }}>
            <Cell>Position</Cell>
            <Cell>Amount</Cell>
            <Cell>Status</Cell>
          </Row>
          <Row>
            <Cell>Collateral</Cell>
            <AmountCell>${userAccountData?.totalCollateralUSD.toFixed(2) || '0.00'}</AmountCell>
            <Cell>
              <span style={{ 
                color: userAccountData && userAccountData.totalDebtUSD === 0 ? '#9ca3af' :
                       userAccountData && userAccountData.healthFactor >= 2 ? '#10b981' : 
                       userAccountData && userAccountData.healthFactor >= 1.5 ? '#f59e0b' : '#ef4444',
                fontSize: '12px',
                fontWeight: 600
              }}>
                {userAccountData && userAccountData.totalDebtUSD === 0 
                  ? 'No Debt' 
                  : `HF: ${userAccountData?.healthFactor.toFixed(2) || '--'}`
                }
              </span>
            </Cell>
          </Row>
          <Row>
            <Cell>Debt</Cell>
            <AmountCell style={{ color: '#a855f7' }}>
              {parseFloat(formatUnits(debtBalance, 6)).toFixed(6)} USDT0
            </AmountCell>
            <Cell>
              <span style={{ 
                color: userAccountData && userAccountData.healthFactor >= 2 ? '#10b981' : 
                       userAccountData && userAccountData.healthFactor >= 1.5 ? '#f59e0b' : '#ef4444',
                fontSize: '11px',
                fontWeight: 600
              }}>
                {userAccountData && userAccountData.healthFactor >= 2 ? 'Safe' : 
                 userAccountData && userAccountData.healthFactor >= 1.5 ? 'Caution' : 'Danger'}
              </span>
            </Cell>
          </Row>

          {/* Collateral Breakdown */}
          {userAccountData && userAccountData.totalCollateralUSD > 0 && (
            <div style={{
              background: '#000000',
              border: '1px solid #000000',
              borderRadius: '8px',
              padding: '12px',
              marginTop: '12px',
              marginBottom: '12px'
            }}>
              <div style={{ fontSize: '12px', color: '#a855f7', fontWeight: 600, marginBottom: '8px' }}>
                💼 Collateral Breakdown
              </div>
              {parseFloat(formatUnits(aTokenBalance, 18)) > 0.0001 && (
                <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px' }}>
                  • aWETH: {parseFloat(formatUnits(aTokenBalance, 18)).toFixed(4)} (${(parseFloat(formatUnits(aTokenBalance, 18)) * ethPrice).toFixed(2)})
                </div>
              )}
              {parseFloat(formatUnits(aUSDT0Balance, 6)) > 0.01 && (
                <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px' }}>
                  • aUSDT0: {parseFloat(formatUnits(aUSDT0Balance, 6)).toFixed(2)} (${parseFloat(formatUnits(aUSDT0Balance, 6)).toFixed(2)})
                </div>
              )}
              {parseFloat(formatUnits(aKBTCBalance, 8)) > 0.00000001 && (
                <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px' }}>
                  • akBTC: {parseFloat(formatUnits(aKBTCBalance, 8)).toFixed(8)} (${(parseFloat(formatUnits(aKBTCBalance, 8)) * btcPrice).toFixed(2)})
                </div>
              )}
              {parseFloat(formatUnits(aTokenBalance, 18)) < 0.0001 && 
               parseFloat(formatUnits(aUSDT0Balance, 6)) < 0.01 && 
               parseFloat(formatUnits(aKBTCBalance, 8)) < 0.00000001 && (
                <div style={{ fontSize: '11px', color: '#6b7280', fontStyle: 'italic' }}>
                  No individual aToken balances detected. Your collateral may be in a different form or there may be a syncing issue.
                </div>
              )}
            </div>
          )}

          {/* Debt Coverage Indicator */}
          {userAccountData && userAccountData.totalDebtUSD > 0 && (
            <DebtCoverageCard $status={debtCoverage.status}>
              <DebtCoverageLabel>
                Debt Coverage (Assets / Debt)
              </DebtCoverageLabel>
              <DebtCoverageValue $status={debtCoverage.status}>
                ${debtCoverage.totalAssets.toFixed(2)} / ${userAccountData.totalDebtUSD.toFixed(2)} ({debtCoverage.coverage.toFixed(0)}%)
              </DebtCoverageValue>
            </DebtCoverageCard>
          )}
        </>
      )}

      {/* Expandable Sections */}
      {/* Supply & Borrow Section */}
      <ExpandableSection show={showSupplyBorrow}>
        <Divider />
        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ color: '#fff', marginBottom: '8px', fontSize: '16px', fontWeight: 600 }}>
            Supply Collateral & Borrow
          </h4>
          
          {/* Step 1: Supply Collateral */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ color: '#9ca3af', fontSize: '13px', display: 'block', marginBottom: '8px' }}>
              Step 1: Supply Collateral
            </label>
            
            {/* Collateral Asset Selector */}
            <AssetToggle>
              <AssetButton $active={supplyAsset === 'ETH'} onClick={() => setSupplyAsset('ETH')}>
                ETH
              </AssetButton>
              <AssetButton $active={supplyAsset === 'WETH'} onClick={() => setSupplyAsset('WETH')}>
                WETH
              </AssetButton>
              <AssetButton $active={supplyAsset === 'USDT0'} onClick={() => setSupplyAsset('USDT0')}>
                USDT0
              </AssetButton>
              <AssetButton $active={supplyAsset === 'kBTC'} onClick={() => setSupplyAsset('kBTC')}>
                kBTC
              </AssetButton>
            </AssetToggle>
            
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="number"
                value={supplyAmount}
                onChange={(e) => setSupplyAmount(e.target.value)}
                placeholder="0.0000"
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid #333333',
                  borderRadius: '8px',
                  color: '#fff',
                  fontFamily: 'monospace'
                }}
              />
              <button
                onClick={() => {
                  if (supplyAsset === 'ETH' && ethBalance) {
                    const balance = parseFloat(formatUnits(ethBalance.value, 18));
                    const maxSupply = Math.max(0, balance - 0.001); // Leave gas
                    setSupplyAmount(maxSupply.toFixed(4));
                  } else if (supplyAsset === 'WETH' && wethBalance) {
                    const balance = parseFloat(formatUnits(wethBalance.value, 18));
                    setSupplyAmount(balance.toFixed(4));
                  } else if (supplyAsset === 'USDT0' && usdtBalance) {
                    const balance = parseFloat(formatUnits(usdtBalance.value, 6));
                    setSupplyAmount(balance.toFixed(2));
                  } else if (supplyAsset === 'kBTC' && kbtcBalance) {
                    const balance = parseFloat(formatUnits(kbtcBalance.value, 8));
                    setSupplyAmount(balance.toFixed(8));
                  }
                }}
                style={{
                  padding: '10px 16px',
                  background: 'rgba(88, 28, 135, 0.8)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                MAX
              </button>
              <button
                onClick={handleSupply}
                disabled={executing || !supplyAmount || parseFloat(supplyAmount) <= 0}
                style={{
                  padding: '10px 16px',
                  background: 'linear-gradient(135deg, #a855f7, #7c3aed)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  opacity: (executing || !supplyAmount || parseFloat(supplyAmount) <= 0) ? 0.5 : 1
                }}
              >
                Supply
              </button>
            </div>
            <p style={{ color: '#6b7280', fontSize: '11px', marginTop: '6px' }}>
              {supplyAsset === 'ETH' 
                ? `Your ETH balance: ${ethBalance ? parseFloat(formatUnits(ethBalance.value, 18)).toFixed(4) : '0.0000'} ETH`
                : supplyAsset === 'WETH'
                ? `Your WETH balance: ${wethBalance ? parseFloat(formatUnits(wethBalance.value, 18)).toFixed(4) : '0.0000'} WETH`
                : supplyAsset === 'USDT0'
                ? `Your USDT0 balance: ${usdtBalance ? parseFloat(formatUnits(usdtBalance.value, 6)).toFixed(2) : '0.00'} USDT0`
                : `Your kBTC balance: ${kbtcBalance ? parseFloat(formatUnits(kbtcBalance.value, 8)).toFixed(8) : '0.00000000'} kBTC`
              }
            </p>
          </div>

          {/* Step 2: Borrow */}
          <div>
            <label style={{ color: '#9ca3af', fontSize: '13px', display: 'block', marginBottom: '8px' }}>
              Step 2: Borrow
            </label>
            
            {/* Asset Selector for Borrowing */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
              <button
                onClick={() => setSelectedBorrowAsset('USDT0')}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: selectedBorrowAsset === 'USDT0' ? 'linear-gradient(135deg, #a855f7, #7c3aed)' : 'rgba(80,80,90,0.4)',
                  border: selectedBorrowAsset === 'USDT0' ? '1px solid #a855f7' : '1px solid rgba(80,80,90,0.6)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                USDT0
              </button>
              <button
                onClick={() => setSelectedBorrowAsset('kBTC')}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: selectedBorrowAsset === 'kBTC' ? 'linear-gradient(135deg, #f7931a, #d97706)' : 'rgba(80,80,90,0.4)',
                  border: selectedBorrowAsset === 'kBTC' ? '1px solid #f7931a' : '1px solid rgba(80,80,90,0.6)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                kBTC
              </button>
            </div>
            
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="number"
                value={borrowAmount}
                onChange={(e) => setBorrowAmount(e.target.value)}
                placeholder="0.00"
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid #333333',
                  borderRadius: '8px',
                  color: '#fff',
                  fontFamily: 'monospace'
                }}
              />
              <button
                onClick={() => {
                  if (userAccountData && calculateSafeBorrowAmount) {
                    const collateralETH = userAccountData.totalCollateralUSD / ethPrice;
                    const safeBorrow = calculateSafeBorrowAmount(collateralETH, ethPrice);
                    setBorrowAmount(safeBorrow.toFixed(2));
                  }
                }}
                disabled={!hasPosition || !userAccountData}
                style={{
                  padding: '10px 16px',
                  background: 'rgba(88, 28, 135, 0.8)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  opacity: (!hasPosition || !userAccountData) ? 0.5 : 1
                }}
              >
                SAFE
              </button>
              <button
                onClick={handleBorrow}
                disabled={executing || !hasPosition || !borrowAmount || parseFloat(borrowAmount) <= 0}
                style={{
                  padding: '10px 16px',
                  background: 'linear-gradient(135deg, #a855f7, #7c3aed)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  opacity: (executing || !hasPosition || !borrowAmount || parseFloat(borrowAmount) <= 0) ? 0.5 : 1
                }}
              >
                Borrow
              </button>
            </div>
            <p style={{ color: '#6b7280', fontSize: '11px', marginTop: '6px' }}>
              {hasPosition && userAccountData ? (
                <>
                  Available to borrow: ${userAccountData.availableBorrowsUSD.toFixed(2)} | 
                  Safe borrow (HF &gt; 2): ${calculateSafeBorrowAmount ? calculateSafeBorrowAmount(userAccountData.totalCollateralUSD / ethPrice, ethPrice).toFixed(2) : '0.00'}
                  {borrowAmount && parseFloat(borrowAmount) > 0 && calculateHealthFactor && (
                    (() => {
                      const collateralETH = userAccountData.totalCollateralUSD / ethPrice;
                      const newTotalDebt = userAccountData.totalDebtUSD + parseFloat(borrowAmount);
                      const newHF = calculateHealthFactor(collateralETH, newTotalDebt, ethPrice);
                      return (
                        <span style={{ 
                          color: newHF >= 2 ? '#10b981' : newHF >= 1.5 ? '#f59e0b' : '#ef4444',
                          fontWeight: 600,
                          marginLeft: '8px'
                        }}>
                          → HF: {newHF.toFixed(2)}
                        </span>
                      );
                    })()
                  )}
                </>
              ) : (
                'Supply collateral first to borrow'
              )}
            </p>
          </div>
        </div>
      </ExpandableSection>

      <ExpandableSection show={!!(showRepay && hasPosition)}>
        <Divider />
        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ color: '#fff', marginBottom: '16px', fontSize: '16px', fontWeight: 600 }}>
            Repay Debt & Withdraw Collateral
          </h4>
          
          {/* Repay Form */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ color: '#9ca3af', fontSize: '13px', display: 'block', marginBottom: '8px' }}>
              Repay USDT0 Debt
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="number"
                value={repayAmount}
                onChange={(e) => setRepayAmount(e.target.value)}
                placeholder="0.00"
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid #333333',
                  borderRadius: '8px',
                  color: '#fff',
                  fontFamily: 'monospace'
                }}
              />
              <button
                onClick={() => {
                  // Use actual debt balance from variable debt token, not USD estimate
                  const actualDebt = parseFloat(formatUnits(debtBalance, 6));
                  
                  if (usdtBalance) {
                    const balance = parseFloat(formatUnits(usdtBalance.value, 6));
                    // Set repay amount to minimum of wallet balance and actual debt
                    setRepayAmount(Math.min(balance, actualDebt).toFixed(6)); // Use 6 decimals for precision
                  } else {
                    // If no wallet balance, just show the debt amount
                    setRepayAmount(actualDebt.toFixed(6));
                  }
                }}
                style={{
                  padding: '10px 16px',
                  background: 'rgba(88, 28, 135, 0.8)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                MAX
              </button>
              <button
                onClick={handleRepay}
                disabled={executing || !repayAmount || parseFloat(repayAmount) <= 0}
                style={{
                  padding: '10px 16px',
                  background: 'rgba(88, 28, 135, 0.8)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  opacity: (executing || !repayAmount || parseFloat(repayAmount) <= 0) ? 0.5 : 1
                }}
              >
                Repay
              </button>
            </div>
            <p style={{ color: '#6b7280', fontSize: '11px', marginTop: '6px' }}>
              Balance: {usdtBalance ? parseFloat(formatUnits(usdtBalance.value, 6)).toFixed(2) : '0.00'} USDT0 | 
              Debt: {parseFloat(formatUnits(debtBalance, 6)).toFixed(6)} USDT0
            </p>
          </div>

                  {/* Withdraw Form */}
                  <div>
                    <label style={{ color: '#9ca3af', fontSize: '13px', display: 'block', marginBottom: '8px' }}>
                      Withdraw Collateral
                    </label>
                    
                    {/* Asset Selector for Withdrawal */}
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                      <button
                        onClick={() => setSelectedWithdrawAsset('WETH')}
                        style={{
                          flex: 1,
                          padding: '10px',
                          background: selectedWithdrawAsset === 'WETH' ? 'linear-gradient(135deg, #a855f7, #7c3aed)' : 'rgba(80,80,90,0.4)',
                          border: selectedWithdrawAsset === 'WETH' ? '1px solid #a855f7' : '1px solid rgba(80,80,90,0.6)',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        WETH
                      </button>
                      <button
                        onClick={() => setSelectedWithdrawAsset('USDT0')}
                        style={{
                          flex: 1,
                          padding: '10px',
                          background: selectedWithdrawAsset === 'USDT0' ? 'linear-gradient(135deg, #10b981, #059669)' : 'rgba(80,80,90,0.4)',
                          border: selectedWithdrawAsset === 'USDT0' ? '1px solid #10b981' : '1px solid rgba(80,80,90,0.6)',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        USDT0
                      </button>
                      <button
                        onClick={() => setSelectedWithdrawAsset('kBTC')}
                        style={{
                          flex: 1,
                          padding: '10px',
                          background: selectedWithdrawAsset === 'kBTC' ? 'linear-gradient(135deg, #f7931a, #d97706)' : 'rgba(80,80,90,0.4)',
                          border: selectedWithdrawAsset === 'kBTC' ? '1px solid #f7931a' : '1px solid rgba(80,80,90,0.6)',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        kBTC
                      </button>
                    </div>
                    
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="number"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                placeholder="0.0000"
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid #333333',
                  borderRadius: '8px',
                  color: '#fff',
                  fontFamily: 'monospace'
                }}
              />
              <button
                onClick={() => {
                  // Calculate MAX based on selected asset (use aToken balance, not wallet balance!)
                  if (selectedWithdrawAsset === 'WETH') {
                    setWithdrawAmount(maxWithdrawableETH.toFixed(4));
                  } else if (selectedWithdrawAsset === 'USDT0') {
                    const balance = parseFloat(formatUnits(aUSDT0Balance, 6));
                    setWithdrawAmount(balance.toFixed(2));
                  } else if (selectedWithdrawAsset === 'kBTC') {
                    const balance = parseFloat(formatUnits(aKBTCBalance, 8));
                    setWithdrawAmount(balance.toFixed(8));
                  }
                }}
                style={{
                  padding: '10px 16px',
                  background: 'rgba(88, 28, 135, 0.8)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
                title="Withdraw maximum available"
              >
                MAX
              </button>
              <button
                onClick={handleWithdraw}
                disabled={executing || !withdrawAmount || parseFloat(withdrawAmount) <= 0}
                style={{
                  padding: '10px 16px',
                  background: 'rgba(88, 28, 135, 0.8)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  opacity: (executing || !withdrawAmount || parseFloat(withdrawAmount) <= 0) ? 0.5 : 1
                }}
              >
                Withdraw
              </button>
            </div>
            <p style={{ color: '#6b7280', fontSize: '11px', marginTop: '6px' }}>
              {selectedWithdrawAsset === 'WETH' ? (
                userAccountData && userAccountData.totalDebtUSD > 0 ?
                  `Max withdrawable: ${maxWithdrawableETH.toFixed(4)} WETH (maintains HF > 2)` :
                  `Collateral: ${formatTokenAmount(parseFloat(formatUnits(aTokenBalance, 18)))} aWETH | Click MAX to withdraw all`
              ) : selectedWithdrawAsset === 'USDT0' ? (
                `Collateral: ${formatTokenAmount(parseFloat(formatUnits(aUSDT0Balance, 6)))} aUSDT0 | Click MAX to withdraw all`
              ) : (
                `Collateral: ${formatTokenAmount(parseFloat(formatUnits(aKBTCBalance, 8)))} akBTC ($${(parseFloat(formatUnits(aKBTCBalance, 8)) * btcPrice).toFixed(2)})`
              )}
            </p>
          </div>
        </div>
      </ExpandableSection>

      {/* Trade Modal - Buy/Sell Combined */}
      {showTradeModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => {
            setShowTradeModal(false);
            setSelectedBuyToken(null);
            setSelectedSellToken(null);
          }}
        >
          <div
            style={{
              background: 'rgba(0, 0, 0, 0.95)',
              borderRadius: '20px',
              padding: '32px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => {
                setShowTradeModal(false);
                setSelectedBuyToken(null);
                setSelectedSellToken(null);
              }}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'transparent',
                border: 'none',
                color: '#fff',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '8px',
                lineHeight: 1
              }}
            >
              ×
            </button>
            <h4 style={{ color: '#fff', marginBottom: '24px', fontSize: '20px', fontWeight: 700 }}>
              Trade Mavrk Tokens
            </h4>

            {/* Buy/Sell Toggle */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '24px' }}>
              <button
                onClick={() => {
                  setTradeMode('buy');
                  setSelectedBuyToken(null);
                  setSelectedSellToken(null);
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: tradeMode === 'buy' ? 'linear-gradient(135deg, #10b981, #059669)' : 'rgba(80,80,90,0.4)',
                  border: tradeMode === 'buy' ? '1px solid #10b981' : '1px solid rgba(80,80,90,0.6)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '16px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Buy Tokens
              </button>
              <button
                onClick={() => {
                  setTradeMode('sell');
                  setSelectedBuyToken(null);
                  setSelectedSellToken(null);
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: tradeMode === 'sell' ? 'linear-gradient(135deg, #ef4444, #dc2626)' : 'rgba(80,80,90,0.4)',
                  border: tradeMode === 'sell' ? '1px solid #ef4444' : '1px solid rgba(80,80,90,0.6)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '16px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Sell Tokens
              </button>
            </div>

            {/* Asset Selector - kBTC or USDT0 */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
              <button
                onClick={() => {
                  if (tradeMode === 'buy') setSelectedBuyAsset('USDT0');
                  else setSelectedSellAsset('USDT0');
                }}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: (tradeMode === 'buy' ? selectedBuyAsset : selectedSellAsset) === 'USDT0' 
                    ? 'linear-gradient(135deg, #a855f7, #7c3aed)' 
                    : 'rgba(80,80,90,0.4)',
                  border: (tradeMode === 'buy' ? selectedBuyAsset : selectedSellAsset) === 'USDT0'
                    ? '1px solid #a855f7' 
                    : '1px solid rgba(80,80,90,0.6)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                USDT0
              </button>
              <button
                onClick={() => {
                  if (tradeMode === 'buy') setSelectedBuyAsset('kBTC');
                  else setSelectedSellAsset('kBTC');
                }}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: (tradeMode === 'buy' ? selectedBuyAsset : selectedSellAsset) === 'kBTC'
                    ? 'linear-gradient(135deg, #f7931a, #d97706)' 
                    : 'rgba(80,80,90,0.4)',
                  border: (tradeMode === 'buy' ? selectedBuyAsset : selectedSellAsset) === 'kBTC'
                    ? '1px solid #f7931a' 
                    : '1px solid rgba(80,80,90,0.6)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                kBTC
              </button>
            </div>

            {/* Buy Mode Content */}
            {tradeMode === 'buy' && (
              !selectedBuyToken ? (
                <>
                  <TokenSearchInput
                    type="text"
                    placeholder="Search tokens by name, symbol, or address..."
                    value={buyTokenSearch}
                    onChange={(e) => setBuyTokenSearch(e.target.value)}
                  />
                  {filteredBuyTokens.length > 0 ? (
                    <TokenList>
                      {filteredBuyTokens.map((token) => (
                        <TokenListItem 
                          key={token.contract_address}
                          onClick={() => {
                            setSelectedBuyToken(token.contract_address!);
                            setBuyTokenSearch('');
                          }}
                        >
                          <TokenIcon src={token.logo_url || '/default-token.png'} alt={token.symbol} />
                          <TokenTextContainer>
                            <TokenName>{token.name}</TokenName>
                            <TokenSymbol>${token.symbol}</TokenSymbol>
                          </TokenTextContainer>
                        </TokenListItem>
                      ))}
                    </TokenList>
                  ) : buyTokenSearch ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                      No tokens found
                    </div>
                  ) : null}
                </>
              ) : (
                <CenteredSwapWrapper>
                  <SelectedTokenCard>
                    <SelectedTokenLogo src={selectedBuyTokenData?.logo_url || '/default-token.png'} alt={selectedBuyTokenData?.symbol} />
                    <SelectedTokenInfo>
                      <SelectedTokenName>{selectedBuyTokenData?.name}</SelectedTokenName>
                      <SelectedTokenSymbol>${selectedBuyTokenData?.symbol}</SelectedTokenSymbol>
                      <SelectedTokenAddress>
                        {selectedBuyToken.slice(0, 6)}...{selectedBuyToken.slice(-4)}
                        <CopyButton onClick={() => {
                          navigator.clipboard.writeText(selectedBuyToken);
                          alert('Contract address copied!');
                        }}>
                          <i className="fas fa-copy"></i> Copy
                        </CopyButton>
                      </SelectedTokenAddress>
                    </SelectedTokenInfo>
                    <button
                      onClick={() => setSelectedBuyToken(null)}
                      style={{
                        background: 'rgba(88, 28, 135, 0.8)',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '8px 12px',
                        color: '#fff',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      Change
                    </button>
                  </SelectedTokenCard>
                  {walletClient && (
                    <SwapWidget
                      wallet={adaptViemWallet(walletClient)}
                      supportedWalletVMs={['evm']}
                      fromToken={{
                        address: selectedBuyAsset === 'USDT0' ? USDT0_ADDRESS : KBTC_ADDRESS,
                        symbol: selectedBuyAsset,
                        name: selectedBuyAsset === 'USDT0' ? 'Bridged Tether' : 'Kraken Wrapped Bitcoin',
                        decimals: selectedBuyAsset === 'USDT0' ? 6 : 8,
                        chainId: 57073,
                        logoURI: selectedBuyAsset === 'USDT0' ? '/USDT0_logo.png' : '/landing-assets/kBTC_logo.png'
                      }}
                      toToken={{
                        address: selectedBuyToken as `0x${string}`,
                        symbol: selectedBuyTokenData?.symbol || '',
                        name: selectedBuyTokenData?.name || '',
                        decimals: 18,
                        chainId: 57073,
                        logoURI: selectedBuyTokenData?.logo_url || '/default-token.png'
                      }}
                      setFromToken={() => {}}
                      setToToken={() => {}}
                      lockChainId={57073}
                      singleChainMode={true}
                      onConnectWallet={openConnectModal}
                      onAnalyticEvent={(eventName, data) => {
                        console.log('Buy Swap:', eventName, data);
                      }}
                      onSwapSuccess={() => {
                        alert(`Successfully bought ${selectedBuyTokenData?.symbol}!`);
                        setRefreshKey(prev => prev + 1);
                      }}
                    />
                  )}
                </CenteredSwapWrapper>
              )
            )}

            {/* Sell Mode Content */}
            {tradeMode === 'sell' && (
              !selectedSellToken ? (
                <>
                  <TokenSearchInput
                    type="text"
                    placeholder="Search tokens by name, symbol, or address..."
                    value={sellTokenSearch}
                    onChange={(e) => setSellTokenSearch(e.target.value)}
                  />
                  {filteredSellTokens.length > 0 ? (
                    <TokenList>
                      {filteredSellTokens.map((token) => (
                        <TokenListItem 
                          key={token.contract_address}
                          onClick={() => {
                            setSelectedSellToken(token.contract_address!);
                            setSellTokenSearch('');
                          }}
                        >
                          <TokenIcon src={token.logo_url || '/default-token.png'} alt={token.symbol} />
                          <TokenTextContainer>
                            <TokenName>{token.name}</TokenName>
                            <TokenSymbol>${token.symbol}</TokenSymbol>
                          </TokenTextContainer>
                        </TokenListItem>
                      ))}
                    </TokenList>
                  ) : sellTokenSearch ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                      No tokens found
                    </div>
                  ) : null}
                </>
              ) : (
                <CenteredSwapWrapper>
                  <SelectedTokenCard>
                    <SelectedTokenLogo src={selectedSellTokenData?.logo_url || '/default-token.png'} alt={selectedSellTokenData?.symbol} />
                    <SelectedTokenInfo>
                      <SelectedTokenName>{selectedSellTokenData?.name}</SelectedTokenName>
                      <SelectedTokenSymbol>${selectedSellTokenData?.symbol}</SelectedTokenSymbol>
                      <SelectedTokenAddress>
                        {selectedSellToken.slice(0, 6)}...{selectedSellToken.slice(-4)}
                        <CopyButton onClick={() => {
                          navigator.clipboard.writeText(selectedSellToken);
                          alert('Contract address copied!');
                        }}>
                          <i className="fas fa-copy"></i> Copy
                        </CopyButton>
                      </SelectedTokenAddress>
                    </SelectedTokenInfo>
                    <button
                      onClick={() => setSelectedSellToken(null)}
                      style={{
                        background: 'rgba(88, 28, 135, 0.8)',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '8px 12px',
                        color: '#fff',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      Change
                    </button>
                  </SelectedTokenCard>
                  {walletClient && (
                    <SwapWidget
                      wallet={adaptViemWallet(walletClient)}
                      supportedWalletVMs={['evm']}
                      fromToken={{
                        address: selectedSellToken as `0x${string}`,
                        symbol: selectedSellTokenData?.symbol || '',
                        name: selectedSellTokenData?.name || '',
                        decimals: 18,
                        chainId: 57073,
                        logoURI: selectedSellTokenData?.logo_url || '/default-token.png'
                      }}
                      toToken={{
                        address: selectedSellAsset === 'USDT0' ? USDT0_ADDRESS : KBTC_ADDRESS,
                        symbol: selectedSellAsset,
                        name: selectedSellAsset === 'USDT0' ? 'Bridged Tether' : 'Kraken Wrapped Bitcoin',
                        decimals: selectedSellAsset === 'USDT0' ? 6 : 8,
                        chainId: 57073,
                        logoURI: selectedSellAsset === 'USDT0' ? '/USDT0_logo.png' : '/landing-assets/kBTC_logo.png'
                      }}
                      setFromToken={() => {}}
                      setToToken={() => {}}
                      lockChainId={57073}
                      singleChainMode={true}
                      onConnectWallet={openConnectModal}
                      onAnalyticEvent={(eventName, data) => {
                        console.log('Sell Swap:', eventName, data);
                      }}
                      onSwapSuccess={() => {
                        alert(`Successfully sold ${selectedSellTokenData?.symbol}!`);
                        setRefreshKey(prev => prev + 1);
                      }}
                    />
                  )}
                </CenteredSwapWrapper>
              )
            )}
          </div>
        </div>
      )}

      {/* Buy Modal */}
      {showBuy && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => setShowBuy(false)}
        >
          <div
            style={{
              background: 'rgba(0, 0, 0, 0.95)',
              borderRadius: '20px',
              padding: '32px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowBuy(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'transparent',
                border: 'none',
                color: '#fff',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '8px',
                lineHeight: 1
              }}
            >
              ×
            </button>
            <h4 style={{ color: '#fff', marginBottom: '24px', fontSize: '20px', fontWeight: 700 }}>
              Buy Mavrk Tokens
            </h4>
          
          {/* Asset Selector */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
            <button
              onClick={() => setSelectedBuyAsset('USDT0')}
              style={{
                flex: 1,
                padding: '10px',
                background: selectedBuyAsset === 'USDT0' ? 'linear-gradient(135deg, #a855f7, #7c3aed)' : 'rgba(80,80,90,0.4)',
                border: selectedBuyAsset === 'USDT0' ? '1px solid #a855f7' : '1px solid rgba(80,80,90,0.6)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              USDT0
            </button>
            <button
              onClick={() => setSelectedBuyAsset('kBTC')}
              style={{
                flex: 1,
                padding: '10px',
                background: selectedBuyAsset === 'kBTC' ? 'linear-gradient(135deg, #f7931a, #d97706)' : 'rgba(80,80,90,0.4)',
                border: selectedBuyAsset === 'kBTC' ? '1px solid #f7931a' : '1px solid rgba(80,80,90,0.6)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              kBTC
            </button>
          </div>
          
          {/* Token Search */}
          {!selectedBuyToken ? (
            <>
              <TokenSearchInput
                type="text"
                placeholder="Search tokens by name, symbol, or address..."
                value={buyTokenSearch}
                onChange={(e) => setBuyTokenSearch(e.target.value)}
              />
              {filteredBuyTokens.length > 0 ? (
                <TokenList>
                  {filteredBuyTokens.map((token) => (
                    <TokenListItem 
                      key={token.contract_address}
                      onClick={() => {
                        setSelectedBuyToken(token.contract_address!);
                        setBuyTokenSearch('');
                      }}
                    >
                      <TokenIcon src={token.logo_url || '/default-token.png'} alt={token.symbol} />
                      <TokenTextContainer>
                        <TokenName>{token.name}</TokenName>
                        <TokenSymbol>${token.symbol}</TokenSymbol>
                      </TokenTextContainer>
                    </TokenListItem>
                  ))}
                </TokenList>
              ) : buyTokenSearch ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                  No tokens found
                </div>
              ) : null}
            </>
          ) : (
            <CenteredSwapWrapper>
              {/* Selected Token Display */}
              <SelectedTokenCard>
                <SelectedTokenLogo src={selectedBuyTokenData?.logo_url || '/default-token.png'} alt={selectedBuyTokenData?.symbol} />
                <SelectedTokenInfo>
                  <SelectedTokenName>{selectedBuyTokenData?.name}</SelectedTokenName>
                  <SelectedTokenSymbol>${selectedBuyTokenData?.symbol}</SelectedTokenSymbol>
                  <SelectedTokenAddress>
                    {selectedBuyToken.slice(0, 6)}...{selectedBuyToken.slice(-4)}
                    <CopyButton onClick={() => {
                      navigator.clipboard.writeText(selectedBuyToken);
                      alert('Contract address copied!');
                    }}>
                      <i className="fas fa-copy"></i> Copy
                    </CopyButton>
                  </SelectedTokenAddress>
                </SelectedTokenInfo>
                <button
                  onClick={() => setSelectedBuyToken(null)}
                  style={{
                    background: 'rgba(88, 28, 135, 0.8)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    color: '#fff',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  Change
                </button>
              </SelectedTokenCard>

              {/* Relay Swap Widget - Selected Asset to Token */}
                {walletClient && (
                  <SwapWidget
                    wallet={adaptViemWallet(walletClient)}
                    supportedWalletVMs={['evm']}
                    fromToken={{
                      address: selectedBuyAsset === 'USDT0' ? USDT0_ADDRESS : KBTC_ADDRESS,
                      symbol: selectedBuyAsset,
                      name: selectedBuyAsset === 'USDT0' ? 'Bridged Tether' : 'Kraken Wrapped Bitcoin',
                      decimals: selectedBuyAsset === 'USDT0' ? 6 : 8,
                      chainId: 57073,
                      logoURI: selectedBuyAsset === 'USDT0' ? '/USDT0_logo.png' : '/landing-assets/kBTC_logo.png'
                    }}
                    toToken={{
                      address: selectedBuyToken as `0x${string}`,
                      symbol: selectedBuyTokenData?.symbol || '',
                      name: selectedBuyTokenData?.name || '',
                      decimals: 18,
                      chainId: 57073,
                      logoURI: selectedBuyTokenData?.logo_url || '/default-token.png'
                    }}
                    setFromToken={() => {}} // Locked to USDT0
                    setToToken={() => {}} // Locked to selected token
                    lockChainId={57073}
                    singleChainMode={true}
                    onConnectWallet={openConnectModal}
                    onAnalyticEvent={(eventName, data) => {
                      console.log('Buy Swap:', eventName, data);
                    }}
                    onSwapSuccess={() => {
                      alert(`Successfully bought ${selectedBuyTokenData?.symbol}!`);
                      setRefreshKey(prev => prev + 1); // Refresh balances after swap
                    }}
                  />
                )}
            </CenteredSwapWrapper>
          )}
          </div>
        </div>
      )}

      {/* Sell Modal */}
      {showSell && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => setShowSell(false)}
        >
          <div
            style={{
              background: 'rgba(0, 0, 0, 0.95)',
              borderRadius: '20px',
              padding: '32px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowSell(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'transparent',
                border: 'none',
                color: '#fff',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '8px',
                lineHeight: 1
              }}
            >
              ×
            </button>
            <h4 style={{ color: '#fff', marginBottom: '24px', fontSize: '20px', fontWeight: 700 }}>
              Sell Mavrk Tokens
            </h4>
          
          {/* Asset Selector */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
            <button
              onClick={() => setSelectedSellAsset('USDT0')}
              style={{
                flex: 1,
                padding: '10px',
                background: selectedSellAsset === 'USDT0' ? 'linear-gradient(135deg, #a855f7, #7c3aed)' : 'rgba(80,80,90,0.4)',
                border: selectedSellAsset === 'USDT0' ? '1px solid #a855f7' : '1px solid rgba(80,80,90,0.6)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              USDT0
            </button>
            <button
              onClick={() => setSelectedSellAsset('kBTC')}
              style={{
                flex: 1,
                padding: '10px',
                background: selectedSellAsset === 'kBTC' ? 'linear-gradient(135deg, #f7931a, #d97706)' : 'rgba(80,80,90,0.4)',
                border: selectedSellAsset === 'kBTC' ? '1px solid #f7931a' : '1px solid rgba(80,80,90,0.6)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              kBTC
            </button>
          </div>
          
          {/* Token Search */}
          {!selectedSellToken ? (
            <>
              <TokenSearchInput
                type="text"
                placeholder="Search tokens by name, symbol, or address..."
                value={sellTokenSearch}
                onChange={(e) => setSellTokenSearch(e.target.value)}
              />
              {filteredSellTokens.length > 0 ? (
                <TokenList>
                  {filteredSellTokens.map((token) => (
                    <TokenListItem 
                      key={token.contract_address}
                      onClick={() => {
                        setSelectedSellToken(token.contract_address!);
                        setSellTokenSearch('');
                      }}
                    >
                      <TokenIcon src={token.logo_url || '/default-token.png'} alt={token.symbol} />
                      <TokenTextContainer>
                        <TokenName>{token.name}</TokenName>
                        <TokenSymbol>${token.symbol}</TokenSymbol>
                      </TokenTextContainer>
                    </TokenListItem>
                  ))}
                </TokenList>
              ) : sellTokenSearch ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                  No tokens found
                </div>
              ) : null}
            </>
          ) : (
            <CenteredSwapWrapper>
              {/* Selected Token Display */}
              <SelectedTokenCard>
                <SelectedTokenLogo src={selectedSellTokenData?.logo_url || '/default-token.png'} alt={selectedSellTokenData?.symbol} />
                <SelectedTokenInfo>
                  <SelectedTokenName>{selectedSellTokenData?.name}</SelectedTokenName>
                  <SelectedTokenSymbol>${selectedSellTokenData?.symbol}</SelectedTokenSymbol>
                  <SelectedTokenAddress>
                    {selectedSellToken.slice(0, 6)}...{selectedSellToken.slice(-4)}
                    <CopyButton onClick={() => {
                      navigator.clipboard.writeText(selectedSellToken);
                      alert('Contract address copied!');
                    }}>
                      <i className="fas fa-copy"></i> Copy
                    </CopyButton>
                  </SelectedTokenAddress>
                </SelectedTokenInfo>
                <button
                  onClick={() => setSelectedSellToken(null)}
                  style={{
                    background: 'rgba(88, 28, 135, 0.8)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    color: '#fff',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  Change
                </button>
              </SelectedTokenCard>

              {/* Relay Swap Widget - Token to Selected Asset */}
                {walletClient && (
                  <SwapWidget
                    wallet={adaptViemWallet(walletClient)}
                    supportedWalletVMs={['evm']}
                    fromToken={{
                      address: selectedSellToken as `0x${string}`,
                      symbol: selectedSellTokenData?.symbol || '',
                      name: selectedSellTokenData?.name || '',
                      decimals: 18,
                      chainId: 57073,
                      logoURI: selectedSellTokenData?.logo_url || '/default-token.png'
                    }}
                    toToken={{
                      address: selectedSellAsset === 'USDT0' ? USDT0_ADDRESS : KBTC_ADDRESS,
                      symbol: selectedSellAsset,
                      name: selectedSellAsset === 'USDT0' ? 'Bridged Tether' : 'Kraken Wrapped Bitcoin',
                      decimals: selectedSellAsset === 'USDT0' ? 6 : 8,
                      chainId: 57073,
                      logoURI: selectedSellAsset === 'USDT0' ? '/USDT0_logo.png' : '/landing-assets/kBTC_logo.png'
                    }}
                    setFromToken={() => {}} // Locked to selected token
                    setToToken={() => {}} // Locked to USDT0
                    lockChainId={57073}
                    singleChainMode={true}
                    onConnectWallet={openConnectModal}
                    onAnalyticEvent={(eventName, data) => {
                      console.log('Sell Swap:', eventName, data);
                    }}
                    onSwapSuccess={() => {
                      alert(`Successfully sold ${selectedSellTokenData?.symbol}!`);
                      setRefreshKey(prev => prev + 1); // Refresh balances after swap
                    }}
                  />
                )}
            </CenteredSwapWrapper>
          )}
          </div>
        </div>
      )}
        </>
      )}

      {/* Supply Modal */}
      {showSupplyModal && supplyModalAsset && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => setShowSupplyModal(false)}
        >
          <div
            style={{
              background: '#000000',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '480px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ position: 'relative', marginBottom: '20px' }}>
              <button
                onClick={() => setShowSupplyModal(false)}
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  background: 'none',
                  border: 'none',
                  color: '#9ca3af',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  lineHeight: 1
                }}
              >
                ×
              </button>
              <h3 style={{ color: '#fff', fontSize: '20px', fontWeight: 700, margin: 0, textAlign: 'center' }}>
                Supply {supplyModalAsset === 'ETH' ? 'Ethereum' :
                       supplyModalAsset === 'WETH' ? 'Wrapped ETH' :
                       supplyModalAsset === 'kBTC' ? 'Kraken Wrapped Bitcoin' :
                       'Bridged Tether'}
              </h3>
              <p style={{ 
                fontSize: '12px', 
                color: '#9ca3af', 
                textAlign: 'center', 
                margin: '8px 0 0 0',
                lineHeight: '1.4'
              }}>
                Supply {supplyModalAsset === 'ETH' ? 'Ethereum' :
                        supplyModalAsset === 'WETH' ? 'Wrapped ETH' :
                        supplyModalAsset === 'kBTC' ? 'Kraken Wrapped Bitcoin' :
                        'Bridged Tether'} to use as collateral for borrowing and leveraged token trading. Your assets automatically accrue interest in Tydro's dynamic lending pools.
              </p>
            </div>

            {/* Available Balance */}
            <div style={{ marginBottom: '16px', padding: '12px', background: 'transparent', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>Available to Supply</div>
              <div style={{ fontSize: '18px', fontWeight: 600, color: '#fff' }}>
                {supplyModalAsset === 'ETH' && ethBalance ? 
                  `${parseFloat(formatUnits(ethBalance.value, 18)).toFixed(4)} ETH` :
                 supplyModalAsset === 'WETH' && wethBalance ?
                  `${parseFloat(formatUnits(wethBalance.value, 18)).toFixed(4)} WETH` :
                 supplyModalAsset === 'kBTC' && kbtcBalance ?
                  `${parseFloat(formatUnits(kbtcBalance.value, 8)).toFixed(8)} kBTC` :
                 supplyModalAsset === 'USDT0' && usdtBalance ?
                  `${parseFloat(formatUnits(usdtBalance.value, 6)).toFixed(2)} USDT0` :
                  '0.00'}
              </div>
              <div style={{ fontSize: '13px', color: '#9ca3af' }}>
                $
                {supplyModalAsset === 'ETH' && ethBalance ?
                  (parseFloat(formatUnits(ethBalance.value, 18)) * ethPrice).toFixed(2) :
                 supplyModalAsset === 'WETH' && wethBalance ?
                  (parseFloat(formatUnits(wethBalance.value, 18)) * ethPrice).toFixed(2) :
                 supplyModalAsset === 'kBTC' && kbtcBalance ?
                  (parseFloat(formatUnits(kbtcBalance.value, 8)) * btcPrice).toFixed(2) :
                 supplyModalAsset === 'USDT0' && usdtBalance ?
                  parseFloat(formatUnits(usdtBalance.value, 6)).toFixed(2) :
                  '0.00'}
              </div>
            </div>

            {/* Supply Amount Input */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <label style={{ fontSize: '14px', color: '#9ca3af' }}>Amount</label>
                <button
                  onClick={() => {
                    if (supplyModalAsset === 'ETH' && ethBalance) {
                      setModalSupplyAmount(parseFloat(formatUnits(ethBalance.value, 18)).toString());
                    } else if (supplyModalAsset === 'WETH' && wethBalance) {
                      setModalSupplyAmount(parseFloat(formatUnits(wethBalance.value, 18)).toString());
                    } else if (supplyModalAsset === 'kBTC' && kbtcBalance) {
                      setModalSupplyAmount(parseFloat(formatUnits(kbtcBalance.value, 8)).toString());
                    } else if (supplyModalAsset === 'USDT0' && usdtBalance) {
                      setModalSupplyAmount(parseFloat(formatUnits(usdtBalance.value, 6)).toString());
                    }
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#a855f7',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  MAX
                </button>
              </div>
              <input
                type="number"
                value={modalSupplyAmount}
                onChange={(e) => setModalSupplyAmount(e.target.value)}
                placeholder="0.00"
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '16px',
                  fontFamily: 'monospace'
                }}
              />
            </div>

            {/* APY Display */}
            <div style={{ marginBottom: '16px', padding: '12px', background: 'transparent', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>Supply APY</div>
              <div style={{ fontSize: '16px', fontWeight: 600, color: '#10b981' }}>
                {(() => {
                  // Map modal asset to protocol stats key
                  const assetKey = supplyModalAsset === 'ETH' || supplyModalAsset === 'WETH' ? 'WETH' :
                                   supplyModalAsset === 'kBTC' ? 'kBTC' :
                                   'USDT0';
                  
                  const supplyAPY = protocolStats?.assetRates?.[assetKey]?.supplyAPY;
                  if (!supplyAPY) return 'Loading...';
                  
                  const apyNum = parseFloat(supplyAPY);
                  return apyNum < 0.01 ? '<0.01%' : `${supplyAPY}%`;
                })()}
              </div>
              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                Earn interest by supplying liquidity (live from protocol)
              </div>
            </div>

            {/* TydroInk Points Estimate */}
            {modalSupplyAmount && parseFloat(modalSupplyAmount) > 0 && (
              <div style={{ 
                marginBottom: '16px', 
                padding: '12px 16px', 
                background: 'transparent', 
                borderRadius: '8px', 
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <img 
                  src="/tydroink.svg" 
                  alt="TydroInk Points"
                  style={{ 
                    width: '24px', 
                    height: '24px',
                    flexShrink: 0
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: '#9333ea' }}>
                    {(() => {
                      const amount = parseFloat(modalSupplyAmount);
                      // Calculate USD value
                      let usdValue = 0;
                      if (supplyModalAsset === 'ETH' || supplyModalAsset === 'WETH') {
                        usdValue = amount * ethPrice;
                      } else if (supplyModalAsset === 'kBTC') {
                        usdValue = amount * btcPrice;
                      } else if (supplyModalAsset === 'USDT0') {
                        usdValue = amount; // USDT0 is $1
                      }
                      
                      // Calculate daily points based on rate per $1k
                      const ratesPer1k = {
                        'ETH': 0.126,
                        'WETH': 0.126,
                        'kBTC': 0.066,
                        'USDT0': 0.192
                      };
                      
                      const rate = ratesPer1k[supplyModalAsset || 'ETH'];
                      const dailyPoints = (usdValue / 1000) * rate;
                      
                      return `${dailyPoints.toFixed(5)} daily`;
                    })()}
                  </div>
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                    TydroInk Points earned
                  </div>
                </div>
              </div>
            )}

            {/* Supply Cap Information */}
            {(() => {
              const assetKey = supplyModalAsset === 'ETH' ? 'WETH' : supplyModalAsset; // ETH uses WETH reserve
              const capInfo = supplyCapData[assetKey || ''];
              const decimals = supplyModalAsset === 'ETH' || supplyModalAsset === 'WETH' ? 18 :
                               supplyModalAsset === 'kBTC' ? 8 : 6;
              
              if (!capInfo) return null;

              const utilizationPct = capInfo.supplyCap > BigInt(0) 
                ? (Number(capInfo.totalSupplied) / Number(capInfo.supplyCap)) * 100 
                : 0;
              
              const isNearingCap = utilizationPct > 90;
              const isCapReached = capInfo.capReached;

              return (
                <div style={{ 
                  marginBottom: '16px', 
                  padding: '12px', 
                  background: 'transparent', 
                  border: 'none', 
                  borderRadius: '8px' 
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span style={{ fontSize: '16px' }}>
                      {isCapReached ? '⛔' : isNearingCap ? '⚠️' : '✅'}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontSize: '12px', 
                        fontWeight: 600, 
                        color: isCapReached ? '#ef4444' : isNearingCap ? '#f59e0b' : '#10b981', 
                        marginBottom: '6px' 
                      }}>
                        {isCapReached ? 'Supply Cap Reached' : isNearingCap ? 'Nearing Supply Cap' : 'Supply Available'}
                      </div>
                      <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '8px' }}>
                        {isCapReached 
                          ? 'This asset has reached its supply cap. No more supply is available at this time.'
                          : `${parseFloat(formatUnits(capInfo.availableToSupply, decimals)).toFixed(decimals === 18 ? 4 : decimals === 8 ? 8 : 2)} ${assetKey} available to supply`
                        }
                      </div>
                      <div style={{ 
                        background: 'transparent', 
                        borderRadius: '4px', 
                        padding: '6px 8px',
                        fontSize: '10px',
                        color: '#9ca3af'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                          <span>Supply Cap:</span>
                          <span style={{ color: '#fff', fontWeight: 600 }}>
                            {parseFloat(formatUnits(capInfo.supplyCap, decimals)).toLocaleString()} {assetKey}
                          </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                          <span>Total Supplied:</span>
                          <span style={{ color: '#fff', fontWeight: 600 }}>
                            {parseFloat(formatUnits(capInfo.totalSupplied, decimals)).toLocaleString()} {assetKey}
                          </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>Utilization:</span>
                          <span style={{ 
                            color: isCapReached ? '#ef4444' : isNearingCap ? '#f59e0b' : '#10b981', 
                            fontWeight: 600 
                          }}>
                            {utilizationPct.toFixed(2)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Supply Button */}
            <button
              onClick={async () => {
                if (!modalSupplyAmount || parseFloat(modalSupplyAmount) <= 0) {
                  alert('Please enter a valid amount');
                  return;
                }
                try {
                  setExecuting(true);
                  if (supplyModalAsset === 'ETH') {
                    await supplyETHCollateral(modalSupplyAmount);
                  } else if (supplyModalAsset === 'WETH') {
                    await supplyWETHCollateral(modalSupplyAmount);
                  } else if (supplyModalAsset === 'kBTC') {
                    await supplyKBTCCollateral(modalSupplyAmount);
                  } else if (supplyModalAsset === 'USDT0') {
                    await supplyUSDT0Collateral(modalSupplyAmount);
                  }
                  alert(`${supplyModalAsset} Supplied!`);
                  setShowSupplyModal(false);
                  setModalSupplyAmount('');
                  await fetchUserAccountData();
                  await fetchATokenBalance();
                  setRefreshKey(prev => prev + 1);
                } catch (error: any) {
                  alert('Transaction Error: Please try again. Check your wallet transaction history for more information.');
                } finally {
                  setExecuting(false);
                }
              }}
              disabled={executing || !modalSupplyAmount || parseFloat(modalSupplyAmount) <= 0}
              style={{
                width: '100%',
                padding: '14px',
                background: executing || !modalSupplyAmount || parseFloat(modalSupplyAmount) <= 0 
                  ? '#333333' 
                  : 'linear-gradient(135deg, #a855f7, #7c3aed)',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '16px',
                fontWeight: 600,
                cursor: executing || !modalSupplyAmount || parseFloat(modalSupplyAmount) <= 0 ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {executing ? 'Supplying...' : `Supply ${supplyModalAsset}`}
            </button>
          </div>
        </div>
      )}

      {/* Borrow Modal */}
      {showBorrowModal && borrowModalAsset && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => setShowBorrowModal(false)}
        >
          <div
            style={{
              background: '#000000',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '480px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ position: 'relative', marginBottom: '20px' }}>
              <button
                onClick={() => setShowBorrowModal(false)}
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  background: 'none',
                  border: 'none',
                  color: '#9ca3af',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  lineHeight: 1
                }}
              >
                ×
              </button>
              <h3 style={{ color: '#fff', fontSize: '20px', fontWeight: 700, margin: 0, textAlign: 'center' }}>
                Borrow
              </h3>
              <p style={{ 
                fontSize: '12px', 
                color: '#9ca3af', 
                textAlign: 'center', 
                margin: '8px 0 0 0',
                lineHeight: '1.4'
              }}>
                Use your collateral to borrow USDT0 or kBTC for leveraged token trading.
              </p>
            </div>

            {/* Asset Selection Buttons */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>Select Asset to Borrow</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setBorrowModalAsset('USDT0');
                    setModalBorrowAmount('');
                  }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: borrowModalAsset === 'USDT0' ? 'linear-gradient(135deg, #10b981, #059669)' : 'rgba(80,80,90,0.4)',
                    border: borrowModalAsset === 'USDT0' ? '1px solid #10b981' : '1px solid rgba(80,80,90,0.6)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  USDT0
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setBorrowModalAsset('kBTC');
                    setModalBorrowAmount('');
                  }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: borrowModalAsset === 'kBTC' ? 'linear-gradient(135deg, #f7931a, #d97706)' : 'rgba(80,80,90,0.4)',
                    border: borrowModalAsset === 'kBTC' ? '1px solid #f7931a' : '1px solid rgba(80,80,90,0.6)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  kBTC
                </button>
              </div>
            </div>

            {/* Collateral Information */}
            <div style={{ marginBottom: '16px', padding: '12px', background: 'transparent', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>Your Collateral</div>
              <div style={{ fontSize: '18px', fontWeight: 600, color: '#fff' }}>
                ${userAccountData?.totalCollateralUSD.toFixed(2) || '0.00'}
              </div>
              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                {userAccountData?.totalDebtUSD 
                  ? `Current debt: $${userAccountData.totalDebtUSD.toFixed(2)}` 
                  : 'No current debt'}
              </div>
            </div>

            {/* Borrow Amount Input */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <label style={{ fontSize: '14px', color: '#9ca3af' }}>Amount to Borrow</label>
                <button
                  onClick={() => {
                    if (!calculateSafeBorrowAmount || !userAccountData) return;
                    // Convert total collateral to ETH equivalent for calculation
                    const collateralETH = userAccountData.totalCollateralUSD / ethPrice;
                    const safeAmountUSD = calculateSafeBorrowAmount(collateralETH, ethPrice);
                    
                    // Convert to asset units
                    const safeAmount = borrowModalAsset === 'kBTC' 
                      ? safeAmountUSD / btcPrice 
                      : safeAmountUSD;
                    
                    setModalBorrowAmount(safeAmount.toFixed(borrowModalAsset === 'kBTC' ? 8 : 2));
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#10b981',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  SAFE
                </button>
              </div>
              <input
                type="number"
                value={modalBorrowAmount}
                onChange={(e) => setModalBorrowAmount(e.target.value)}
                placeholder="0.00"
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '16px',
                  fontFamily: 'monospace'
                }}
              />
              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px' }}>
                Safe borrow maintains Health Factor {'>'} 2.0
              </div>
            </div>

            {/* Borrow APY Display */}
            <div style={{ marginBottom: '16px', padding: '12px', background: 'transparent', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>Borrow APY</div>
              <div style={{ fontSize: '16px', fontWeight: 600, color: '#ef4444' }}>
                {(() => {
                  const assetKey = borrowModalAsset === 'kBTC' ? 'kBTC' : 'USDT0';
                  const borrowAPY = protocolStats?.assetRates?.[assetKey]?.borrowAPY;
                  if (!borrowAPY) return 'Loading...';
                  
                  const apyNum = parseFloat(borrowAPY);
                  return apyNum < 0.01 ? '<0.01%' : `${borrowAPY}%`;
                })()}
              </div>
              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                Interest rate on borrowed amount (live from protocol)
              </div>
            </div>

            {/* Health Factor Warning */}
            {userAccountData && (
              <div style={{ marginBottom: '16px', padding: '12px', background: 'transparent', border: 'none', borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <span style={{ fontSize: '16px' }}>⚠️</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#f59e0b', marginBottom: '4px' }}>
                      Maintain Safe Health Factor
                    </div>
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '6px' }}>
                      Keep your Health Factor above 2.0 to avoid liquidation risk. Use the SAFE button for recommended amount.
                    </div>
                    {modalBorrowAmount && parseFloat(modalBorrowAmount) > 0 && (
                      <div style={{ 
                        background: 'rgba(0, 0, 0, 0.2)', 
                        borderRadius: '4px', 
                        padding: '6px 8px',
                        fontSize: '10px',
                        color: '#9ca3af'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>Estimated HF after borrow:</span>
                          <span style={{ 
                            color: (() => {
                              if (!userAccountData || !calculateHealthFactor) return '#9ca3af';
                              const newHF = calculateHealthFactor(
                                userAccountData.totalCollateralUSD / ethPrice,
                                userAccountData.totalDebtUSD + parseFloat(modalBorrowAmount),
                                ethPrice
                              );
                              return newHF >= 2 ? '#10b981' : newHF >= 1.5 ? '#f59e0b' : '#ef4444';
                            })(),
                            fontWeight: 600 
                          }}>
                            {(() => {
                              if (!userAccountData || !calculateHealthFactor) return '--';
                              const newHF = calculateHealthFactor(
                                userAccountData.totalCollateralUSD / ethPrice,
                                userAccountData.totalDebtUSD + parseFloat(modalBorrowAmount),
                                ethPrice
                              );
                              return newHF.toFixed(2);
                            })()}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Borrow Button */}
            <button
              onClick={async () => {
                if (!modalBorrowAmount || parseFloat(modalBorrowAmount) <= 0) {
                  alert('Please enter a valid amount');
                  return;
                }
                try {
                  setExecuting(true);
                  if (borrowModalAsset === 'USDT0') {
                    await borrowUSDT(modalBorrowAmount);
                  } else if (borrowModalAsset === 'kBTC') {
                    await borrowAsset(KBTC_ADDRESS, modalBorrowAmount, 8, 'kBTC');
                  }
                  alert(`${borrowModalAsset} Borrowed!`);
                  setShowBorrowModal(false);
                  setModalBorrowAmount('');
                  await fetchUserAccountData();
                  await fetchDebtBalance();
                  setRefreshKey(prev => prev + 1);
                } catch (error: any) {
                  alert('Transaction Error: Please try again. Check your wallet transaction history for more information.');
                } finally {
                  setExecuting(false);
                }
              }}
              disabled={executing || !modalBorrowAmount || parseFloat(modalBorrowAmount) <= 0}
              style={{
                width: '100%',
                padding: '14px',
                background: executing || !modalBorrowAmount || parseFloat(modalBorrowAmount) <= 0 
                  ? 'rgba(16, 185, 129, 0.3)' 
                  : 'linear-gradient(135deg, #10b981, #059669)',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '16px',
                fontWeight: 600,
                cursor: executing || !modalBorrowAmount || parseFloat(modalBorrowAmount) <= 0 ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {executing ? 'Borrowing...' : `Borrow ${borrowModalAsset}`}
            </button>
          </div>
        </div>
      )}

      {/* Unified Manage Modal (Repay & Withdraw) */}
      {showManageModal && manageModalAsset && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => setShowManageModal(false)}
        >
          <div
            style={{
              background: '#000000',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '480px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ position: 'relative', marginBottom: '20px' }}>
              <button
                onClick={() => setShowManageModal(false)}
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  background: 'none',
                  border: 'none',
                  color: '#9ca3af',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  lineHeight: 1
                }}
              >
                ×
              </button>
              <h3 style={{ color: '#fff', fontSize: '20px', fontWeight: 700, margin: 0, textAlign: 'center' }}>
                Manage Position
              </h3>
              <p style={{ 
                fontSize: '12px', 
                color: '#9ca3af', 
                textAlign: 'center', 
                margin: '8px 0 16px 0',
                lineHeight: '1.4'
              }}>
                {manageModalAsset === 'WETH' ? 'WETH Collateral' :
                 manageModalAsset === 'kBTC' ? 'kBTC Collateral' :
                 'USDT0 Collateral'}
              </p>
              
              {/* Tab Buttons */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <button
                  onClick={() => setManageModalTab('repay')}
                  disabled={!userAccountData || userAccountData.totalDebtUSD === 0}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: manageModalTab === 'repay' 
                      ? 'linear-gradient(135deg, #a855f7, #7c3aed)' 
                      : '#000000',
                    border: manageModalTab === 'repay' ? '2px solid #a855f7' : '2px solid transparent',
                    borderRadius: '8px',
                    color: manageModalTab === 'repay' ? '#fff' : '#9ca3af',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: (!userAccountData || userAccountData.totalDebtUSD === 0) ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: (!userAccountData || userAccountData.totalDebtUSD === 0) ? 0.5 : 1
                  }}
                >
                  Repay Debt
                </button>
                <button
                  onClick={() => setManageModalTab('withdraw')}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: manageModalTab === 'withdraw' 
                      ? 'linear-gradient(135deg, #f59e0b, #d97706)' 
                      : 'rgba(245, 158, 11, 0.2)',
                    border: manageModalTab === 'withdraw' ? '2px solid #f59e0b' : '2px solid transparent',
                    borderRadius: '8px',
                    color: manageModalTab === 'withdraw' ? '#fff' : '#9ca3af',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Withdraw Collateral
                </button>
              </div>
            </div>

            {/* REPAY TAB CONTENT */}
            {manageModalTab === 'repay' && (
              <>
                {/* Current Debt Display */}
                <div style={{ marginBottom: '16px', padding: '12px', background: 'transparent', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>Current Debt</div>
              <div style={{ fontSize: '18px', fontWeight: 600, color: '#ef4444' }}>
                {parseFloat(formatUnits(debtBalance, 6)).toFixed(6)} USDT0
              </div>
              <div style={{ fontSize: '13px', color: '#9ca3af' }}>
                ${parseFloat(formatUnits(debtBalance, 6)).toFixed(2)} USD
              </div>
            </div>

            {/* USDT0 Balance Display */}
            <div style={{ marginBottom: '16px', padding: '12px', background: 'transparent', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>Available to Repay</div>
              <div style={{ fontSize: '18px', fontWeight: 600, color: '#fff' }}>
                {usdtBalance ? parseFloat(formatUnits(usdtBalance.value, 6)).toFixed(2) : '0.00'} USDT0
              </div>
              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                Balance in your wallet
              </div>
            </div>

            {/* Repay Amount Input */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <label style={{ fontSize: '14px', color: '#9ca3af' }}>Amount to Repay</label>
                <button
                  onClick={() => {
                    if (!usdtBalance) return;
                    const walletBalance = parseFloat(formatUnits(usdtBalance.value, 6));
                    const debt = parseFloat(formatUnits(debtBalance, 6));
                    setModalRepayAmount(Math.min(walletBalance, debt).toFixed(6));
                  }}
                  title="Repay full debt (handles accrued interest automatically)"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#a855f7',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  MAX
                </button>
              </div>
              <input
                type="number"
                value={modalRepayAmount}
                onChange={(e) => setModalRepayAmount(e.target.value)}
                placeholder="0.00"
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '16px',
                  fontFamily: 'monospace'
                }}
              />
              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px' }}>
                💡 Click MAX to repay full debt (automatically handles accrued interest)
              </div>
            </div>

            {/* Health Factor Info */}
            {userAccountData && userAccountData.totalDebtUSD > 0 && (
              <div style={{ marginBottom: '16px', padding: '12px', background: 'transparent', borderRadius: '8px' }}>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '6px' }}>
                  Current Health Factor: <span style={{ color: '#10b981', fontWeight: 600 }}>{userAccountData.healthFactor.toFixed(2)}</span>
                </div>
                <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                  Repaying debt improves your Health Factor and reduces liquidation risk.
                </div>
              </div>
            )}

            {/* Repay Button */}
            <button
              onClick={async () => {
                if (!modalRepayAmount || parseFloat(modalRepayAmount) <= 0) {
                  alert('Please enter a valid amount');
                  return;
                }
                try {
                  setExecuting(true);
                  const debt = parseFloat(formatUnits(debtBalance, 6));
                  const repayAmount = parseFloat(modalRepayAmount);
                  
                  // If repaying >= 99.9% of debt, treat as repayAll to handle accrued interest
                  const repayAll = repayAmount >= (debt * 0.999);
                  
                  console.log(`[Repay] Amount: ${repayAmount}, Debt: ${debt}, RepayAll: ${repayAll}`);
                  
                  await repayUSDT(modalRepayAmount, repayAll);
                  alert('USDT0 Repaid!');
                  setShowManageModal(false);
                  setModalRepayAmount('');
                  await fetchUserAccountData();
                  await fetchDebtBalance();
                  setRefreshKey(prev => prev + 1);
                } catch (error: any) {
                  alert('Transaction Error: Please try again. Check your wallet transaction history for more information.');
                } finally {
                  setExecuting(false);
                }
              }}
              disabled={executing || !modalRepayAmount || parseFloat(modalRepayAmount) <= 0}
              style={{
                width: '100%',
                padding: '14px',
                background: executing || !modalRepayAmount || parseFloat(modalRepayAmount) <= 0 
                  ? '#333333' 
                  : 'linear-gradient(135deg, #a855f7, #7c3aed)',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '16px',
                fontWeight: 600,
                cursor: executing || !modalRepayAmount || parseFloat(modalRepayAmount) <= 0 ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {executing ? 'Repaying...' : 'Repay USDT0'}
            </button>
              </>
            )}

            {/* WITHDRAW TAB CONTENT */}
            {manageModalTab === 'withdraw' && (
              <>
                {/* Collateral Balance Display */}
                <div style={{ marginBottom: '16px', padding: '12px', background: 'transparent', borderRadius: '8px' }}>
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>Available Collateral</div>
                  <div style={{ fontSize: '18px', fontWeight: 600, color: '#fff' }}>
                    {manageModalAsset === 'WETH' ? 
                      `${parseFloat(formatUnits(aTokenBalance, 18)).toFixed(4)} aWETH` :
                     manageModalAsset === 'kBTC' ?
                      `${parseFloat(formatUnits(aKBTCBalance, 8)).toFixed(8)} akBTC` :
                      `${parseFloat(formatUnits(aUSDT0Balance, 6)).toFixed(2)} aUSDT0`}
                  </div>
                  <div style={{ fontSize: '13px', color: '#9ca3af' }}>
                    $
                    {manageModalAsset === 'WETH' ?
                      (parseFloat(formatUnits(aTokenBalance, 18)) * ethPrice).toFixed(2) :
                     manageModalAsset === 'kBTC' ?
                      (parseFloat(formatUnits(aKBTCBalance, 8)) * btcPrice).toFixed(2) :
                      parseFloat(formatUnits(aUSDT0Balance, 6)).toFixed(2)}
                  </div>
                </div>

                {/* Withdraw Amount Input */}
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <label style={{ fontSize: '14px', color: '#9ca3af' }}>Amount to Withdraw</label>
                    <button
                      onClick={() => {
                        if (manageModalAsset === 'WETH') {
                          setModalWithdrawAmount(parseFloat(formatUnits(aTokenBalance, 18)).toFixed(4));
                        } else if (manageModalAsset === 'kBTC') {
                          setModalWithdrawAmount(parseFloat(formatUnits(aKBTCBalance, 8)).toFixed(8));
                        } else if (manageModalAsset === 'USDT0') {
                          setModalWithdrawAmount(parseFloat(formatUnits(aUSDT0Balance, 6)).toFixed(2));
                        }
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#f59e0b',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      MAX
                    </button>
                  </div>
                  <input
                    type="number"
                    value={modalWithdrawAmount}
                    onChange={(e) => setModalWithdrawAmount(e.target.value)}
                    placeholder="0.00"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '16px',
                      fontFamily: 'monospace'
                    }}
                  />
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px' }}>
                    {userAccountData && userAccountData.totalDebtUSD > 0 
                      ? 'Repay all debt before withdrawing full collateral'
                      : 'No debt - you can withdraw all collateral'}
                  </div>
                </div>

                {/* Health Factor Warning (if debt exists) */}
                {userAccountData && userAccountData.totalDebtUSD > 0 && (
                  <div style={{ marginBottom: '16px', padding: '12px', background: 'transparent', border: 'none', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      <span style={{ fontSize: '16px' }}>⚠️</span>
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#f59e0b', marginBottom: '4px' }}>
                          Maintain Safe Health Factor
                        </div>
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                          Keep your Health Factor above 1.5 when withdrawing. Current HF: {userAccountData.healthFactor.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Withdraw Button */}
                <button
                  onClick={async () => {
                    if (!modalWithdrawAmount || parseFloat(modalWithdrawAmount) <= 0) {
                      alert('Please enter a valid amount');
                      return;
                    }
                    try {
                      setExecuting(true);
                      const assetAddress = manageModalAsset === 'WETH' ? WETH_ADDRESS 
                                          : manageModalAsset === 'USDT0' ? USDT0_ADDRESS 
                                          : KBTC_ADDRESS;
                      const decimals = manageModalAsset === 'WETH' ? 18 
                                      : manageModalAsset === 'USDT0' ? 6 
                                      : 8;
                      
                      await withdrawAsset(assetAddress, modalWithdrawAmount, decimals, manageModalAsset, false);
                      alert(`${manageModalAsset} Withdrawn!`);
                      setShowManageModal(false);
                      setModalWithdrawAmount('');
                      await fetchUserAccountData();
                      await fetchATokenBalance();
                      setRefreshKey(prev => prev + 1);
                    } catch (error: any) {
                      alert('Transaction Error: Please try again. Check your wallet transaction history for more information.');
                    } finally {
                      setExecuting(false);
                    }
                  }}
                  disabled={executing || !modalWithdrawAmount || parseFloat(modalWithdrawAmount) <= 0}
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: executing || !modalWithdrawAmount || parseFloat(modalWithdrawAmount) <= 0 
                      ? 'rgba(245, 158, 11, 0.3)' 
                      : 'linear-gradient(135deg, #f59e0b, #d97706)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '16px',
                    fontWeight: 600,
                    cursor: executing || !modalWithdrawAmount || parseFloat(modalWithdrawAmount) <= 0 ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {executing ? 'Withdrawing...' : `Withdraw ${manageModalAsset}`}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default TydroDashboard;
